/**
 * The golf shop's own settings, and what they earned.
 *
 * Seventeen golf endpoints existed before this screen and six of them had a
 * way in. The rate card the board votes on every spring, the seasons that
 * decide what time the first group goes off, the fleet, the lesson book and
 * the revenue split between Golf Operations and Golf Carts were all built,
 * tested, and unreachable. This is the way in.
 *
 * Four things are drawn rather than explained:
 *
 *   A rate is a dated row, so it has two different edits and they are
 *   offered as two different buttons. AMEND is "the board raised the guest
 *   fee" — a new row from a date, the old one keeping its period, every
 *   round charged last week still explainable. CORRECT is "somebody typed
 *   1250 for 125" — the row was never right, so superseding it would leave
 *   the wrong figure in force for the window it was live. Correcting asks
 *   why and reports how many charges fell in that window.
 *
 *   Delete is offered where the row has no consequences and withheld where
 *   it has. The server decides, not this screen — and when it refuses, its
 *   sentence is shown as written, because "that cart has been out 1 time(s)
 *   … set it to retired instead" is the whole answer and a generic failure
 *   message would throw it away.
 *
 *   Editing a season does not relay a sheet already opened for those dates.
 *   The API returns how many days are affected and that is said out loud,
 *   because the alternative is a professional discovering it on Saturday.
 *
 *   Revenue is split by department, not summed. Cart fees landing in Golf
 *   Operations rather than Golf Carts is the most common way a club loses
 *   track of whether its fleet washes its face.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { api } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { useSession } from '../lib/session.tsx';
import { Card, Empty, ErrorBanner, Loading, Money, Status, Tile } from '../components/ui.tsx';
import { PersonLookup, type PersonHit } from '../components/Lookup.tsx';
import {
  CourseAlertBanner, useCourseAlerts, type AlertsPayload,
} from '../components/CourseAlert.tsx';
import {
  CourseConditions, CONDITION_LABEL,
} from '../components/CourseConditions.tsx';
import {
  Leaderboard, FORMAT_LABEL, type CompetitionRow,
} from '../components/Leaderboard.tsx';
import {
  Standings, points as seriesPoints, type SeriesRow,
} from '../components/Standings.tsx';
import { Stickers } from '../components/Stickers.tsx';
import { LiveBoard } from '../components/LiveBoard.tsx';
import { date as fmtDate, dateTime, parseMoney, money, titleCase } from '../lib/format.ts';

/* ------------------------------------------------------------------ */
/* Shapes                                                              */
/* ------------------------------------------------------------------ */

interface Season {
  id: string;
  name: string;
  starts_on: string;
  ends_on: string;
  interval_minutes: number;
  first_tee_time: string;
  last_tee_time: string;
  max_players_per_slot: number;
  note: string | null;
}

interface Rate {
  id: string;
  fee_kind: string;
  rate_class: string;
  day_type: string | null;
  holes: number | null;
  amount_minor: number;
  revenue_category: string;
  department_code: string | null;
  department_name: string | null;
  effective_from: string;
  effective_to: string | null;
  note: string | null;
}

interface Cart {
  cart_id: string;
  cart_number: string;
  kind: string;
  seats: number;
  status: string;
  is_out: boolean;
  play_date: string | null;
  starts_at: string | null;
  out_with: string | null;
}

interface Lesson {
  lesson_id: string;
  professional_name: string;
  professional_title: string | null;
  student_name: string | null;
  starts_at: string;
  duration_minutes: number;
  kind: string;
  status: string;
  amount_minor: number | null;
  ar_transaction_id: string | null;
}

interface RevenueLine {
  department_code: string;
  department_name: string;
  revenue_category: string;
  charge_count: number;
  revenue_minor: string;
}

type Tab = 'today' | 'events' | 'series' | 'seasons' | 'rates' | 'fleet' | 'stickers'
  | 'lessons' | 'practice' | 'numbers' | 'revenue' | 'devices' | 'scores'
  | 'crew';

const TABS: { id: Tab; label: string }[] = [
  { id: 'today', label: 'Today on the course' },
  { id: 'crew', label: 'Course & crew' },
  { id: 'events', label: 'Events' },
  { id: 'series', label: 'Orders of merit' },
  { id: 'stickers', label: 'Stickers & codes' },
  { id: 'practice', label: 'Practice center' },
  { id: 'numbers', label: 'The numbers' },
  { id: 'seasons', label: 'Hours & seasons' },
  { id: 'rates', label: 'Rate card' },
  { id: 'fleet', label: 'Cart fleet' },
  { id: 'lessons', label: 'Lesson book' },
  { id: 'scores', label: 'Cards & handicaps' },
  { id: 'revenue', label: 'What golf earned' },
];

/**
 * The devices tab is club_admin's and is added to the list only for
 * them, rather than shown and refused. A tab that 403s is a tab that
 * looks broken.
 *
 * It lives on the golf screen because golf is what needed it first and
 * the lightning detector is the only thing that holds one today. When a
 * door panel or a gateway takes one too, this belongs in settings — the
 * table (0083) was written general on purpose and this component moves
 * without changing.
 */
const DEVICES_TAB: { id: Tab; label: string } = { id: 'devices', label: 'Devices' };

/** Postgres hands back 09:00:00; a tee sheet says 09:00. */
const hhmm = (t: string) => t.slice(0, 5);
const today = () => new Date().toISOString().slice(0, 10);
/** A season defaults to this calendar year, which is what most clubs run. */
const yearStart = () => `${new Date().getFullYear()}-01-01`;
const yearEnd = () => `${new Date().getFullYear()}-12-31`;
const monthStart = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

/* ------------------------------------------------------------------ */
/* Shared bits                                                         */
/* ------------------------------------------------------------------ */

/**
 * Delete, once. Two clicks, and the second one is labelled with what it
 * does rather than "OK" — a row of identical confirm dialogs is a row of
 * things people stop reading.
 */
function DeleteButton({
  label = 'Delete',
  confirmLabel,
  onDelete,
  busy,
}: {
  label?: string;
  confirmLabel: string;
  onDelete: () => void;
  busy?: boolean;
}) {
  const [armed, setArmed] = useState(false);
  if (!armed) {
    return (
      <button className="secondary small" onClick={() => setArmed(true)}>
        {label}
      </button>
    );
  }
  return (
    <>
      <button className="danger small" disabled={busy} onClick={onDelete}>
        {busy ? 'Working…' : confirmLabel}
      </button>
      <button className="secondary small" onClick={() => setArmed(false)}>
        Keep
      </button>
    </>
  );
}

function Actions({ children }: { children: ReactNode }) {
  return <div className="actions">{children}</div>;
}

/* ------------------------------------------------------------------ */
/* Hours & seasons                                                     */
/* ------------------------------------------------------------------ */

const BLANK_SEASON = {
  name: '',
  starts_on: '',
  ends_on: '',
  interval_minutes: '8',
  first_tee_time: '07:00',
  last_tee_time: '18:30',
  max_players_per_slot: '4',
};

function SeasonForm({
  initial,
  submitLabel,
  onSubmit,
  onCancel,
  busy,
  error,
}: {
  initial: typeof BLANK_SEASON;
  submitLabel: string;
  onSubmit: (v: typeof BLANK_SEASON) => void;
  onCancel: () => void;
  busy: boolean;
  error: ReturnType<typeof useMutation>['error'];
}) {
  const [f, setF] = useState(initial);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(f);
      }}
    >
      <ErrorBanner error={error} />
      <div className="grid-2">
        <label className="field">
          <span>Name</span>
          <input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        </label>
        <label className="field">
          <span>Starts</span>
          <input required type="date" value={f.starts_on}
            onChange={(e) => setF({ ...f, starts_on: e.target.value })} />
        </label>
        <label className="field">
          <span>Ends</span>
          <input required type="date" value={f.ends_on}
            onChange={(e) => setF({ ...f, ends_on: e.target.value })} />
        </label>
        <label className="field">
          <span>Interval (minutes)</span>
          <input required type="number" min={5} max={60} value={f.interval_minutes}
            onChange={(e) => setF({ ...f, interval_minutes: e.target.value })} />
        </label>
        <label className="field">
          <span>First tee</span>
          <input required type="time" value={f.first_tee_time}
            onChange={(e) => setF({ ...f, first_tee_time: e.target.value })} />
        </label>
        <label className="field">
          <span>Last tee</span>
          <input required type="time" value={f.last_tee_time}
            onChange={(e) => setF({ ...f, last_tee_time: e.target.value })} />
        </label>
        <label className="field">
          <span>Players per slot</span>
          <input required type="number" min={1} max={8} value={f.max_players_per_slot}
            onChange={(e) => setF({ ...f, max_players_per_slot: e.target.value })} />
        </label>
      </div>
      <div className="actions">
        <button className="gold" disabled={busy}>{busy ? 'Saving…' : submitLabel}</button>
        <button type="button" className="secondary" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

function Seasons({ canEdit }: { canEdit: boolean }) {
  const q = useApi<{ seasons: Season[] }>('/v1/golf/seasons');
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const body = (v: typeof BLANK_SEASON) => ({
    name: v.name,
    starts_on: v.starts_on,
    ends_on: v.ends_on,
    interval_minutes: Number(v.interval_minutes),
    first_tee_time: v.first_tee_time,
    last_tee_time: v.last_tee_time,
    max_players_per_slot: Number(v.max_players_per_slot),
  });

  const create = useMutation(async (v: typeof BLANK_SEASON) => {
    await api('/v1/golf/seasons', { method: 'POST', body: body(v) });
    setAdding(false);
    q.reload();
  });

  const update = useMutation(async (id: string, v: typeof BLANK_SEASON) => {
    const r = await api<{ days_already_laid_out: number }>(`/v1/golf/seasons/${id}`, {
      method: 'PATCH',
      body: body(v),
    });
    setEditing(null);
    // The one consequence a professional would otherwise meet on Saturday.
    setNote(
      r.days_already_laid_out > 0
        ? `Saved. ${r.days_already_laid_out} day(s) of tee sheet were already laid out inside ` +
          `this season and keep the interval they were opened with — reopening those days ` +
          `is the only way to relay them.`
        : 'Saved.',
    );
    q.reload();
  });

  const remove = useMutation(async (id: string) => {
    await api(`/v1/golf/seasons/${id}`, { method: 'DELETE' });
    q.reload();
  });

  if (q.loading) return <Loading rows={4} />;
  if (q.error) return <ErrorBanner error={q.error} />;

  const seasons = [...(q.data?.seasons ?? [])].sort((a, b) =>
    a.starts_on.localeCompare(b.starts_on),
  );

  const gaps: { after: string; from: string; to: string }[] = [];
  for (let i = 1; i < seasons.length; i++) {
    const prevEnd = new Date(seasons[i - 1]!.ends_on);
    const dayAfter = new Date(prevEnd.getTime() + 86_400_000).toISOString().slice(0, 10);
    if (dayAfter < seasons[i]!.starts_on) {
      gaps.push({ after: seasons[i - 1]!.name, from: dayAfter, to: seasons[i]!.starts_on });
    }
  }

  return (
    <>
      {gaps.length > 0 && (
        <Card title="Days the Sheet Cannot Be Opened">
          <p className="muted">
            The seasons do not meet. On these dates there is no interval or first tee time to lay
            a sheet out from, and opening one will be refused.
          </p>
          <ul>
            {gaps.map((g) => (
              <li key={g.from}>
                <strong>{fmtDate(g.from)}</strong> until <strong>{fmtDate(g.to)}</strong>{' '}
                <span className="muted">— after {g.after} ends</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card
        title="Hours & Seasons"
        actions={
          canEdit && !adding && (
            <button className="secondary small" onClick={() => setAdding(true)}>Add a season</button>
          )
        }
      >
        <p className="muted">
          A season sets the interval between tee times and the first and last time of the day.
          The sheet is laid out from whichever season the date falls in, so these are the numbers
          that decide how many rounds a day can hold.
        </p>

        {note && <div className="notice">{note}</div>}
        <ErrorBanner error={remove.error} />

        {adding && (
          <SeasonForm
            initial={BLANK_SEASON}
            submitLabel="Add season"
            busy={create.busy}
            error={create.error}
            onSubmit={(v) => void create.run(v)}
            onCancel={() => setAdding(false)}
          />
        )}

        {seasons.length === 0 ? (
          <Empty>No seasons yet. The sheet cannot be opened until there is one.</Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Season</th><th>From</th><th>To</th>
                <th className="num">Interval</th><th>First tee</th><th>Last tee</th>
                <th className="num">Per slot</th><th className="num">Rounds/day</th>
                {canEdit && <th />}
              </tr>
            </thead>
            <tbody>
              {seasons.map((s) => {
                const [fh, fm] = s.first_tee_time.split(':').map(Number);
                const [lh, lm] = s.last_tee_time.split(':').map(Number);
                const slots = Math.floor(((lh! * 60 + lm!) - (fh! * 60 + fm!)) / s.interval_minutes) + 1;
                const isNow = s.starts_on <= today() && s.ends_on >= today();

                if (editing === s.id) {
                  return (
                    <tr key={s.id}>
                      <td colSpan={canEdit ? 9 : 8}>
                        <SeasonForm
                          initial={{
                            name: s.name,
                            starts_on: s.starts_on,
                            ends_on: s.ends_on,
                            interval_minutes: String(s.interval_minutes),
                            first_tee_time: hhmm(s.first_tee_time),
                            last_tee_time: hhmm(s.last_tee_time),
                            max_players_per_slot: String(s.max_players_per_slot),
                          }}
                          submitLabel="Save season"
                          busy={update.busy}
                          error={update.error}
                          onSubmit={(v) => void update.run(s.id, v)}
                          onCancel={() => setEditing(null)}
                        />
                      </td>
                    </tr>
                  );
                }

                return (
                  <tr key={s.id}>
                    <td>{s.name} {isNow && <Status tone="good">in force</Status>}</td>
                    <td>{fmtDate(s.starts_on)}</td>
                    <td>{fmtDate(s.ends_on)}</td>
                    <td className="num">{s.interval_minutes} min</td>
                    <td>{hhmm(s.first_tee_time)}</td>
                    <td>{hhmm(s.last_tee_time)}</td>
                    <td className="num">{s.max_players_per_slot}</td>
                    <td className="num">{slots * s.max_players_per_slot}</td>
                    {canEdit && (
                      <td>
                        <Actions>
                          <button className="secondary small" onClick={() => { setNote(null); setEditing(s.id); }}>
                            Edit
                          </button>
                          <DeleteButton
                            confirmLabel="Delete season"
                            busy={remove.busy}
                            onDelete={() => void remove.run(s.id)}
                          />
                        </Actions>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Rate card                                                           */
/* ------------------------------------------------------------------ */

function Rates({ canEdit }: { canEdit: boolean }) {
  const { currency } = useSession();
  const [on, setOn] = useState(today());
  const q = useApi<{ rates: Rate[] }>(`/v1/golf/rates?on=${on}`, [on]);

  const [amending, setAmending] = useState<Rate | null>(null);
  const [correcting, setCorrecting] = useState<Rate | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const [amount, setAmount] = useState('');
  const [from, setFrom] = useState(today());
  const [reason, setReason] = useState('');

  const amend = useMutation(async (r: Rate) => {
    const minor = parseMoney(amount, currency);
    if (minor === null) throw new Error('amount');
    await api('/v1/golf/rates', {
      method: 'POST',
      body: {
        fee_kind: r.fee_kind, rate_class: r.rate_class, day_type: r.day_type ?? 'any',
        holes: r.holes, amount_minor: minor, revenue_category: r.revenue_category,
        effective_from: from, supersede: true,
      },
    });
    setAmending(null);
    setNote(`New price from ${fmtDate(from)}. The old one keeps its period.`);
    q.reload();
  });

  const correct = useMutation(async (r: Rate) => {
    const minor = parseMoney(amount, currency);
    if (minor === null) throw new Error('amount');
    const res = await api<{ charges_in_the_affected_window: number }>(`/v1/golf/rates/${r.id}`, {
      method: 'PATCH',
      body: { amount_minor: minor, reason },
    });
    setCorrecting(null);
    setReason('');
    const n = res.charges_in_the_affected_window;
    setNote(
      n > 0
        ? `Corrected. Roughly ${n} charge(s) were posted in that category while the wrong ` +
          `figure was in force — worth checking whether any need crediting.`
        : 'Corrected. Nothing was charged while the wrong figure was in force.',
    );
    q.reload();
  });

  const remove = useMutation(async (id: string) => {
    await api(`/v1/golf/rates/${id}`, { method: 'DELETE' });
    q.reload();
  });

  const rates = q.data?.rates ?? [];
  const byKind = new Map<string, Rate[]>();
  for (const r of rates) {
    const list = byKind.get(r.fee_kind) ?? [];
    list.push(r);
    byKind.set(r.fee_kind, list);
  }

  const startAmend = (r: Rate) => {
    setNote(null); setCorrecting(null); setAmending(r);
    setAmount(money(r.amount_minor, currency).replace(/[^0-9.]/g, ''));
    setFrom(today());
  };
  const startCorrect = (r: Rate) => {
    setNote(null); setAmending(null); setCorrecting(r);
    setAmount(money(r.amount_minor, currency).replace(/[^0-9.]/g, ''));
    setReason('');
  };

  return (
    <Card
      title="Rate Card"
      actions={
        <label className="field" style={{ margin: 0 }}>
          <span>In force on</span>
          <input type="date" value={on} onChange={(e) => setOn(e.target.value)} />
        </label>
      }
    >
      <p className="muted">
        A rate is a dated row, so there are two different edits. <strong>Amend</strong> is a new
        price from a date — the old row keeps its period, and last week's rounds stay explainable.
        <strong> Correct</strong> is for a row that was never right; it asks why, and reports what
        was charged while the mistake was live.
      </p>

      {note && <div className="notice">{note}</div>}
      <ErrorBanner error={remove.error} />

      {amending && (
        <div className="card__body" style={{ border: '1px solid var(--hairline)', borderRadius: 'var(--radius-sm)', padding: 16, marginBottom: 16 }}>
          <h3>
            Amend — {titleCase(amending.rate_class.replace(/_/g, ' '))}{' '}
            {amending.day_type && titleCase(amending.day_type)}{' '}
            {amending.holes ? `${amending.holes} holes` : ''}
          </h3>
          <ErrorBanner error={amend.error} />
          <div className="grid-2">
            <label className="field">
              <span>New amount</span>
              <input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
            </label>
            <label className="field">
              <span>From</span>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </label>
          </div>
          <Actions>
            <button className="gold" disabled={amend.busy} onClick={() => void amend.run(amending)}>
              {amend.busy ? 'Saving…' : 'Set the new price'}
            </button>
            <button className="secondary" onClick={() => setAmending(null)}>Cancel</button>
          </Actions>
        </div>
      )}

      {correcting && (
        <div className="card__body" style={{ border: '1px solid var(--hairline)', borderRadius: 'var(--radius-sm)', padding: 16, marginBottom: 16 }}>
          <h3>Correct a Mistake</h3>
          <p className="muted small">
            This changes the figure that was in force from {fmtDate(correcting.effective_from)}.
            Use it when the row was wrong, not when the price changed.
          </p>
          <ErrorBanner error={correct.error} />
          <div className="grid-2">
            <label className="field">
              <span>Correct amount</span>
              <input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
            </label>
            <label className="field">
              <span>Why (recorded against your name)</span>
              <input required minLength={3} value={reason} placeholder="Typed 1250 for 125"
                onChange={(e) => setReason(e.target.value)} />
            </label>
          </div>
          <Actions>
            <button className="gold" disabled={correct.busy || reason.trim().length < 3}
              onClick={() => void correct.run(correcting)}>
              {correct.busy ? 'Saving…' : 'Correct it'}
            </button>
            <button className="secondary" onClick={() => setCorrecting(null)}>Cancel</button>
          </Actions>
        </div>
      )}

      {q.loading ? (
        <Loading rows={5} />
      ) : q.error ? (
        <ErrorBanner error={q.error} />
      ) : rates.length === 0 ? (
        <Empty>Nothing is priced on {fmtDate(on)}. A round booked that day cannot be charged.</Empty>
      ) : (
        [...byKind.entries()].map(([kind, list]) => (
          <div key={kind} style={{ marginTop: 18 }}>
            <h3>{titleCase(kind.replace(/_/g, ' '))}</h3>
            <table className="data">
              <thead>
                <tr>
                  <th>Who</th><th>Day</th><th>Holes</th>
                  <th className="num">Amount</th><th>Posts to</th><th>Since</th>
                  {canEdit && <th />}
                </tr>
              </thead>
              <tbody>
                {list.map((r) => (
                  <tr key={r.id}>
                    <td>{titleCase(r.rate_class.replace(/_/g, ' '))}</td>
                    <td>{r.day_type ? titleCase(r.day_type) : <span className="muted">any</span>}</td>
                    <td>{r.holes ?? <span className="muted">18 / any</span>}</td>
                    <td className="num"><Money minor={r.amount_minor} currency={currency} /></td>
                    <td>
                      {r.department_code
                        ? `${r.department_code} ${r.department_name}`
                        : <span className="muted">—</span>}
                    </td>
                    <td>
                      {fmtDate(r.effective_from)}
                      {r.effective_to && <span className="muted"> until {fmtDate(r.effective_to)}</span>}
                    </td>
                    {canEdit && (
                      <td>
                        <Actions>
                          <button className="secondary small" onClick={() => startAmend(r)}>Amend</button>
                          <button className="secondary small" onClick={() => startCorrect(r)}>Correct</button>
                          <DeleteButton
                            confirmLabel="Delete rate"
                            busy={remove.busy}
                            onDelete={() => void remove.run(r.id)}
                          />
                        </Actions>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))
      )}
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Cart fleet                                                          */
/* ------------------------------------------------------------------ */

const CART_KINDS = ['electric', 'gas', 'push', 'trail'];
const CART_STATUS = ['available', 'maintenance', 'retired'];

function Fleet({ canEdit, canDelete }: { canEdit: boolean; canDelete: boolean }) {
  const q = useApi<{ carts: Cart[]; out: number; available: number }>('/v1/golf/carts');
  const [editing, setEditing] = useState<string | null>(null);
  const [f, setF] = useState({ cart_number: '', kind: 'electric', seats: '2', status: 'available' });

  const update = useMutation(async (id: string) => {
    await api(`/v1/golf/carts/${id}`, {
      method: 'PATCH',
      body: {
        cart_number: f.cart_number, kind: f.kind,
        seats: Number(f.seats), status: f.status,
      },
    });
    setEditing(null);
    q.reload();
  });

  const remove = useMutation(async (id: string) => {
    await api(`/v1/golf/carts/${id}`, { method: 'DELETE' });
    q.reload();
  });

  if (q.loading) return <Loading rows={4} />;
  if (q.error) return <ErrorBanner error={q.error} />;

  const carts = q.data?.carts ?? [];
  const shop = carts.filter((c) => c.status !== 'available').length;

  return (
    <>
      <div className="tiles">
        <Tile label="Out on the course" value={q.data?.out ?? 0} />
        <Tile label="Ready" value={q.data?.available ?? 0} />
        <Tile label="Not available" value={shop}
          note={shop > 0 ? 'in the shop or retired' : undefined}
          tone={shop > 0 ? 'warning' : undefined} />
        <Tile label="Fleet size" value={carts.length} />
      </div>

      <Card title="Cart Fleet">
        <ErrorBanner error={update.error} />
        <ErrorBanner error={remove.error} />
        {carts.length === 0 ? (
          <Empty>No carts on the books.</Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Cart</th><th>Kind</th><th className="num">Seats</th>
                <th>Status</th><th>Out with</th>{canEdit && <th />}
              </tr>
            </thead>
            <tbody>
              {carts.map((c) =>
                editing === c.cart_id ? (
                  <tr key={c.cart_id}>
                    <td>
                      <input value={f.cart_number} style={{ width: 80 }}
                        onChange={(e) => setF({ ...f, cart_number: e.target.value })} />
                    </td>
                    <td>
                      <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}>
                        {CART_KINDS.map((k) => <option key={k} value={k}>{titleCase(k)}</option>)}
                      </select>
                    </td>
                    <td className="num">
                      <input type="number" min={1} max={6} value={f.seats} style={{ width: 60 }}
                        onChange={(e) => setF({ ...f, seats: e.target.value })} />
                    </td>
                    <td>
                      <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
                        {CART_STATUS.map((s) => <option key={s} value={s}>{titleCase(s)}</option>)}
                      </select>
                    </td>
                    <td />
                    <td>
                      <Actions>
                        <button className="gold small" disabled={update.busy}
                          onClick={() => void update.run(c.cart_id)}>
                          {update.busy ? 'Saving…' : 'Save'}
                        </button>
                        <button className="secondary small" onClick={() => setEditing(null)}>Cancel</button>
                      </Actions>
                    </td>
                  </tr>
                ) : (
                  <tr key={c.cart_id}>
                    <td>{c.cart_number}</td>
                    <td>{titleCase(c.kind.replace(/_/g, ' '))}</td>
                    <td className="num">{c.seats}</td>
                    <td>
                      {c.is_out ? <Status tone="warning">out</Status>
                        : c.status === 'available' ? <Status tone="good">ready</Status>
                        : <Status tone="serious">{titleCase(c.status)}</Status>}
                    </td>
                    <td>
                      {c.is_out ? (
                        <>
                          {c.out_with ?? 'a group'}{' '}
                          <span className="muted">{c.play_date && fmtDate(c.play_date)} {c.starts_at}</span>
                        </>
                      ) : <span className="muted">—</span>}
                    </td>
                    {canEdit && (
                      <td>
                        <Actions>
                          <button className="secondary small" onClick={() => {
                            setF({
                              cart_number: c.cart_number, kind: c.kind,
                              seats: String(c.seats), status: c.status,
                            });
                            setEditing(c.cart_id);
                          }}>Edit</button>
                          {canDelete && (
                            <DeleteButton
                              confirmLabel="Delete cart"
                              busy={remove.busy}
                              onDelete={() => void remove.run(c.cart_id)}
                            />
                          )}
                        </Actions>
                      </td>
                    )}
                  </tr>
                ),
              )}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Lesson book                                                         */
/* ------------------------------------------------------------------ */

function Lessons({ canEdit }: { canEdit: boolean }) {
  const { currency } = useSession();
  const [from, setFrom] = useState(today());
  const q = useApi<{ lessons: Lesson[] }>(`/v1/golf/lessons?from=${from}`, [from]);
  const [editing, setEditing] = useState<string | null>(null);
  const [f, setF] = useState({ starts_at: '', duration_minutes: '60' });

  const update = useMutation(async (id: string, patch: Record<string, unknown>) => {
    await api(`/v1/golf/lessons/${id}`, { method: 'PATCH', body: patch });
    setEditing(null);
    q.reload();
  });

  const complete = useMutation(async (id: string) => {
    await api(`/v1/golf/lessons/${id}/complete`, { method: 'POST', body: {} });
    q.reload();
  });

  const lessons = q.data?.lessons ?? [];
  const unbilled = lessons.filter((l) => l.status === 'completed' && !l.ar_transaction_id);

  return (
    <Card
      title="Lesson Book"
      actions={
        <label className="field" style={{ margin: 0 }}>
          <span>From</span>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
      }
    >
      {unbilled.length > 0 && (
        <p>
          <Status tone="warning">
            {unbilled.length} completed {unbilled.length === 1 ? 'lesson has' : 'lessons have'} not been charged
          </Status>
        </p>
      )}
      <ErrorBanner error={update.error} />
      <ErrorBanner error={complete.error} />

      {q.loading ? (
        <Loading rows={4} />
      ) : q.error ? (
        <ErrorBanner error={q.error} />
      ) : lessons.length === 0 ? (
        <Empty>Nothing in the book from {fmtDate(from)}.</Empty>
      ) : (
        <table className="data">
          <thead>
            <tr>
              <th>When</th><th>Professional</th><th>Student</th><th>Kind</th>
              <th className="num">Minutes</th><th>Status</th>
              <th className="num">Fee</th>{canEdit && <th />}
            </tr>
          </thead>
          <tbody>
            {lessons.map((l) =>
              editing === l.lesson_id ? (
                <tr key={l.lesson_id}>
                  <td>
                    <input type="datetime-local" value={f.starts_at}
                      onChange={(e) => setF({ ...f, starts_at: e.target.value })} />
                  </td>
                  <td colSpan={3}>{l.professional_name}</td>
                  <td className="num">
                    <input type="number" min={15} max={480} value={f.duration_minutes} style={{ width: 70 }}
                      onChange={(e) => setF({ ...f, duration_minutes: e.target.value })} />
                  </td>
                  <td colSpan={2} />
                  <td>
                    <Actions>
                      <button className="gold small" disabled={update.busy}
                        onClick={() => void update.run(l.lesson_id, {
                          starts_at: new Date(f.starts_at).toISOString(),
                          duration_minutes: Number(f.duration_minutes),
                        })}>
                        {update.busy ? 'Saving…' : 'Save'}
                      </button>
                      <button className="secondary small" onClick={() => setEditing(null)}>Cancel</button>
                    </Actions>
                  </td>
                </tr>
              ) : (
                <tr key={l.lesson_id}>
                  <td>{dateTime(l.starts_at)}</td>
                  <td>
                    {l.professional_name}
                    {l.professional_title && <span className="muted"> · {l.professional_title}</span>}
                  </td>
                  <td>{l.student_name ?? <span className="muted">—</span>}</td>
                  <td>{titleCase(l.kind.replace(/_/g, ' '))}</td>
                  <td className="num">{l.duration_minutes}</td>
                  <td>
                    {l.status === 'completed' && !l.ar_transaction_id
                      ? <Status tone="warning">uncharged</Status>
                      : <Status tone={l.status === 'completed' ? 'good' : 'neutral'}>
                          {titleCase(l.status)}
                        </Status>}
                  </td>
                  <td className="num">
                    {l.amount_minor === null
                      ? <span className="muted">—</span>
                      : <Money minor={l.amount_minor} currency={currency} />}
                  </td>
                  {canEdit && (
                    <td>
                      <Actions>
                        {l.status === 'scheduled' && (
                          <>
                            <button className="secondary small" onClick={() => {
                              setF({
                                starts_at: l.starts_at.slice(0, 16),
                                duration_minutes: String(l.duration_minutes),
                              });
                              setEditing(l.lesson_id);
                            }}>Reschedule</button>
                            <DeleteButton
                              label="Cancel lesson"
                              confirmLabel="Cancel it"
                              busy={update.busy}
                              onDelete={() => void update.run(l.lesson_id, { status: 'canceled' })}
                            />
                          </>
                        )}
                        {l.status === 'completed' && !l.ar_transaction_id && (
                          <button className="gold small" disabled={complete.busy}
                            onClick={() => void complete.run(l.lesson_id)}>
                            Charge it
                          </button>
                        )}
                      </Actions>
                    </td>
                  )}
                </tr>
              ),
            )}
          </tbody>
        </table>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Revenue                                                             */
/* ------------------------------------------------------------------ */

function Revenue() {
  const { currency } = useSession();
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const q = useApi<{ lines: RevenueLine[]; total_minor: number }>(
    `/v1/golf/revenue?from=${from}&to=${to}`, [from, to],
  );

  if (q.loading) return <Loading rows={4} />;
  if (q.error) return <ErrorBanner error={q.error} />;

  const lines = q.data?.lines ?? [];
  const byDept = new Map<string, { name: string; minor: number; count: number }>();
  for (const l of lines) {
    const cur = byDept.get(l.department_code) ?? { name: l.department_name, minor: 0, count: 0 };
    cur.minor += Number(l.revenue_minor);
    cur.count += l.charge_count;
    byDept.set(l.department_code, cur);
  }

  return (
    <Card
      title="What Golf Earned"
      actions={
        <div className="row">
          <label className="field" style={{ margin: 0 }}>
            <span>From</span>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label className="field" style={{ margin: 0 }}>
            <span>To</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </label>
        </div>
      }
    >
      <p className="muted">
        Read back out of the ledger rather than added up from the tee sheet, so this is what the
        accounts say and not what the shop believes. The split matters: a cart fee that lands in
        Golf Operations makes the fleet look like it earns nothing.
      </p>

      <div className="tiles">
        {[...byDept.entries()].map(([code, d]) => (
          <Tile key={code} label={`${code} ${d.name}`}
            value={<Money minor={d.minor} currency={currency} />}
            note={`${d.count} ${d.count === 1 ? 'charge' : 'charges'}`} />
        ))}
        <Tile label="Total" value={<Money minor={q.data?.total_minor ?? 0} currency={currency} />} />
      </div>

      {lines.length === 0 ? (
        <Empty>Nothing posted between {fmtDate(from)} and {fmtDate(to)}.</Empty>
      ) : (
        <table className="data">
          <thead>
            <tr><th>Department</th><th>Category</th><th className="num">Charges</th><th className="num">Revenue</th></tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={i}>
                <td>{l.department_code} {l.department_name}</td>
                <td>{titleCase(l.revenue_category.replace(/_/g, ' '))}</td>
                <td className="num">{l.charge_count}</td>
                <td className="num"><Money minor={l.revenue_minor} currency={currency} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Today on the course                                                 */
/*                                                                     */
/* The half past five screen. Three panels in the order the decision   */
/* is actually made: what the weather did, what the club's own rules   */
/* make of that, and then — and only then — what the professional      */
/* writes down.                                                        */
/*                                                                     */
/* The proposal is deliberately drawn as an argument rather than an    */
/* answer. "Delayed till 09:30" on its own is something a person       */
/* either obeys or ignores; "dawn low 26°F, your hard frost rule wants */
/* 28°F" is something they can agree with, and the difference is       */
/* whether the feature survives its first wrong morning.               */
/* ------------------------------------------------------------------ */

interface Reading {
  local_time: string;
  observed_at: string;
  condition_code: string | null;
  temp: string | null;
  temp_unit: string;
  precip: string | null;
  precip_unit: string;
  wind: string | null;
  wind_unit: string;
  gust: string | null;
  wind_dir_deg: number | null;
  humidity_pct: number | null;
  cloud_pct: number | null;
}

interface WeatherPayload {
  play_date: string;
  is_today: boolean;
  timezone: string;
  measurement_system: string;
  located: boolean;
  source: string;
  last_reading_at: string | null;
  observations_held: number;
  observed: Reading[];
  forecast: (Reading & { local_date: string; forecast_made_at: string })[];
}

interface FiredRule {
  rule_name: string;
  metric: string;
  comparator: string;
  threshold: string;
  observed: string;
  proposes_status: string;
  proposes_cart: string;
  proposes_first_tee_at: string | null;
  reason: string;
  advice: string | null;
}

interface CourseDay {
  id: string;
  play_date: string;
  play_status: string;
  cart_policy: string;
  first_tee_at: string | null;
  reason: string | null;
  note: string | null;
  decided_at: string | null;
  decided_by_name: string | null;
  slots: number;
}

interface CourseDaysPayload {
  course_id: string;
  today: string;
  from: string;
  to: string;
  days: CourseDay[];
  decided_today: boolean;
  proposal: {
    has_weather: boolean;
    rules_fired: number;
    play_status: string;
    cart_policy: string;
    first_tee_at: string | null;
    reason: string | null;
    rules: FiredRule[];
  };
}

interface ConditionRule {
  id: string;
  course_id: string | null;
  course_name: string | null;
  name: string;
  metric: string;
  comparator: string;
  threshold: string;
  proposes_status: string;
  proposes_cart: string;
  proposes_first_tee_at: string | null;
  reason: string;
  advice: string | null;
  from_month: number | null;
  to_month: number | null;
  is_active: boolean;
  sort_order: number;
}

const PLAY_STATUS: Record<string, string> = {
  open: 'Open',
  delayed: 'Delayed',
  closed: 'Closed',
};

const CART_POLICY: Record<string, string> = {
  normal: 'Carts normal',
  path_only: 'Carts on the path',
  none: 'Carts in',
};

const METRIC_LABEL: Record<string, string> = {
  dawn_min_temp_c: 'Lowest temperature at dawn',
  dawn_mean_wind_kph: 'Average wind at dawn',
  day_max_temp_c: 'Highest temperature',
  day_precip_mm: 'Rain over the day',
};

const REASONS = ['frost', 'rain', 'snow', 'wind', 'lightning', 'maintenance', 'event', 'other'];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * The status a day is in, as a coloured word.
 *
 * Closed is critical and delayed is a warning rather than both being red:
 * a frost delay is a normal Tuesday in March and colouring it as an
 * emergency teaches people to ignore the colour.
 */
function DayStatus({ status, cart }: { status: string; cart: string }) {
  const tone =
    status === 'closed' ? 'critical'
      : status === 'delayed' ? 'warning'
        : cart !== 'normal' ? 'neutral' : 'good';
  const words = status === 'open' && cart === 'normal'
    ? 'Open, carts normal'
    : `${PLAY_STATUS[status] ?? status}${cart === 'normal' ? '' : ` · ${CART_POLICY[cart] ?? cart}`}`;
  return <Status tone={tone as never}>{words}</Status>;
}

/** A reading, or a dash. An empty cell reads as zero. */
const val = (n: string | null | undefined, unit: string) =>
  n === null || n === undefined ? '—' : `${n}${unit === '°F' || unit === '°C' ? unit : ` ${unit}`}`;

function WeatherPanel({ w }: { w: WeatherPayload }) {
  if (!w.located) {
    return (
      <Card title="Weather">
        <div className="notice">
          <strong>This club has no coordinates, so nothing is being collected.</strong>
          <div style={{ marginTop: 4 }}>
            Set the latitude and longitude on the club record and the poller picks it up on its
            next pass. Until then this screen would show calm, dry weather at every hour, which
            is not the same thing as no weather.
          </div>
        </div>
      </Card>
    );
  }

  if (w.observed.length === 0) {
    return (
      <Card title="Weather">
        <Empty>
          No readings for {fmtDate(w.play_date)} yet.
          {w.last_reading_at
            ? ` The last one anywhere was ${dateTime(w.last_reading_at)}.`
            : ' Nothing has ever been collected for this club.'}
        </Empty>
      </Card>
    );
  }

  // Dawn is 05:00–09:00 local, the same window every rule reads. Showing
  // the club a different dawn from the one the rules used would be the
  // sort of quiet disagreement nobody finds for a year.
  const dawn = w.observed.filter((o) => {
    const h = Number(o.local_time.slice(0, 2));
    return h >= 5 && h <= 9;
  });
  const nums = (rs: Reading[], k: 'temp' | 'wind') =>
    rs.map((r) => (r[k] === null ? null : Number(r[k]))).filter((n): n is number => n !== null);

  const dawnTemps = nums(dawn, 'temp');
  const dayTemps = nums(w.observed, 'temp');
  const dawnWind = nums(dawn, 'wind');
  const rain = w.observed
    .map((o) => (o.precip === null ? 0 : Number(o.precip)))
    .reduce((a, b) => a + b, 0);

  const latest = w.observed[w.observed.length - 1]!;
  const unit = latest.temp_unit;

  return (
    <Card
      title="Weather"
      actions={
        <span className="muted small">
          {w.source.toUpperCase()} · {w.observations_held} reading
          {w.observations_held === 1 ? '' : 's'} held
        </span>
      }
    >
      <div className="tiles">
        <Tile
          label={`Now (${hhmm(latest.local_time)})`}
          value={val(latest.temp, unit)}
          note={`${val(latest.wind, latest.wind_unit)}${
            latest.gust ? `, gusting ${val(latest.gust, latest.wind_unit)}` : ''
          }`}
        />
        <Tile
          label="Lowest at dawn"
          value={dawnTemps.length ? val(String(Math.min(...dawnTemps)), unit) : '—'}
          note="05:00–09:00, the window the rules read"
        />
        <Tile
          label="Highest today"
          value={dayTemps.length ? val(String(Math.max(...dayTemps)), unit) : '—'}
        />
        <Tile
          label="Wind at dawn"
          value={
            dawnWind.length
              ? val((dawnWind.reduce((a, b) => a + b, 0) / dawnWind.length).toFixed(1),
                latest.wind_unit)
              : '—'
          }
        />
        <Tile
          label="Rain today"
          value={val(rain.toFixed(2), latest.precip_unit)}
          note={`${w.observed.length} hour${w.observed.length === 1 ? '' : 's'} recorded`}
        />
      </div>

      <details>
        <summary className="muted small" style={{ cursor: 'pointer', marginBottom: 8 }}>
          Hour by hour
        </summary>
        <table className="data">
          <thead>
            <tr>
              <th>Time</th>
              <th className="num">Temp</th>
              <th className="num">Wind</th>
              <th className="num">Gust</th>
              <th className="num">Rain</th>
              <th className="num">Humidity</th>
              <th className="num">Cloud</th>
            </tr>
          </thead>
          <tbody>
            {w.observed.map((o) => (
              <tr key={o.observed_at}>
                <td className="mono">{hhmm(o.local_time)}</td>
                <td className="num">{val(o.temp, o.temp_unit)}</td>
                <td className="num">{val(o.wind, o.wind_unit)}</td>
                <td className="num">{val(o.gust, o.wind_unit)}</td>
                <td className="num">{val(o.precip, o.precip_unit)}</td>
                <td className="num">{o.humidity_pct === null ? '—' : `${o.humidity_pct}%`}</td>
                <td className="num">{o.cloud_pct === null ? '—' : `${o.cloud_pct}%`}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </Card>
  );
}

/**
 * What the rules make of the morning, shown as reasoning.
 *
 * Never stored anywhere. Editing a threshold changes what this panel says
 * and changes nothing about any morning the club has already decided —
 * which is the property that makes "how often was the software right?"
 * a question with an answer.
 */
function ProposalPanel({ p }: { p: CourseDaysPayload['proposal'] }) {
  if (!p.has_weather) {
    return (
      <Card title="What the Rules Say">
        <Empty>
          Nothing to go on. No readings have come in for today, so the rules have not been
          applied — this is not the same as the rules having looked and found nothing wrong.
        </Empty>
      </Card>
    );
  }

  if (p.rules_fired === 0) {
    return (
      <Card title="What the Rules Say">
        <p className="muted">
          Nothing fired. Every threshold the club has set was read against this morning's
          conditions and none of them were crossed.
        </p>
      </Card>
    );
  }

  return (
    <Card title="What the Rules Say">
      <div style={{ marginBottom: 12 }}>
        <DayStatus status={p.play_status} cart={p.cart_policy} />
        {p.first_tee_at && (
          <span className="muted" style={{ marginLeft: 10 }}>
            first tee {hhmm(p.first_tee_at)}
          </span>
        )}
      </div>

      <p className="muted small">
        A suggestion, not a decision. {p.rules_fired} rule
        {p.rules_fired === 1 ? '' : 's'} fired — the course is not delayed until somebody says
        it is.
      </p>

      <table className="data">
        <thead>
          <tr>
            <th>Rule</th>
            <th>Looked at</th>
            <th className="num">Wanted</th>
            <th className="num">Got</th>
            <th>Suggests</th>
          </tr>
        </thead>
        <tbody>
          {p.rules.map((r, i) => (
            <tr key={i}>
              <td>
                <strong>{r.rule_name}</strong>
                {r.advice && <div className="hint">{r.advice}</div>}
              </td>
              <td>{METRIC_LABEL[r.metric] ?? r.metric}</td>
              <td className="num mono">
                {r.comparator === 'below' ? '<' : '>'} {r.threshold}
              </td>
              <td className="num mono">{r.observed}</td>
              <td>
                <DayStatus status={r.proposes_status} cart={r.proposes_cart} />
                {r.proposes_first_tee_at && (
                  <div className="hint">till {hhmm(r.proposes_first_tee_at)}</div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

const BLANK_DECISION = {
  play_date: '',
  play_status: 'open',
  cart_policy: 'normal',
  first_tee_at: '',
  reason: '',
  note: '',
};

function DecisionForm({
  initial,
  submitLabel,
  onSubmit,
  onCancel,
  busy,
  error,
}: {
  initial: typeof BLANK_DECISION;
  submitLabel: string;
  onSubmit: (v: typeof BLANK_DECISION) => void;
  onCancel?: () => void;
  busy: boolean;
  error: ReturnType<typeof useMutation>['error'];
}) {
  const [f, setF] = useState(initial);
  const ordinary = f.play_status === 'open' && f.cart_policy === 'normal';

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(f);
      }}
    >
      <ErrorBanner error={error} />
      <div className="grid-2">
        <label className="field">
          <span>Day</span>
          <input required type="date" value={f.play_date}
            onChange={(e) => setF({ ...f, play_date: e.target.value })} />
        </label>
        <label className="field">
          <span>Play</span>
          <select value={f.play_status}
            onChange={(e) => setF({ ...f, play_status: e.target.value })}>
            <option value="open">Open</option>
            <option value="delayed">Delayed</option>
            <option value="closed">Closed</option>
          </select>
        </label>
        <label className="field">
          <span>Carts</span>
          <select value={f.cart_policy}
            onChange={(e) => setF({ ...f, cart_policy: e.target.value })}>
            <option value="normal">Normal</option>
            <option value="path_only">On the path</option>
            <option value="none">In</option>
          </select>
        </label>
        {f.play_status === 'delayed' && (
          <label className="field">
            <span>First tee</span>
            <input required type="time" value={f.first_tee_at}
              onChange={(e) => setF({ ...f, first_tee_at: e.target.value })} />
            <div className="hint">When play began, or is expected to.</div>
          </label>
        )}
        {!ordinary && (
          <label className="field">
            <span>Reason</span>
            <select required value={f.reason}
              onChange={(e) => setF({ ...f, reason: e.target.value })}>
              <option value="">Pick one</option>
              {REASONS.map((r) => (
                <option key={r} value={r}>{titleCase(r)}</option>
              ))}
            </select>
          </label>
        )}
      </div>
      <label className="field">
        <span>Note</span>
        <input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })}
          placeholder="Walked 4 and 13 at six. Still white in the hollow." />
        <div className="hint">
          What you saw. In a year this is the only part nobody can reconstruct.
        </div>
      </label>
      <div className="actions">
        <button className="gold" disabled={busy}>{busy ? 'Saving…' : submitLabel}</button>
        {onCancel && (
          <button type="button" className="secondary" onClick={onCancel}>Cancel</button>
        )}
      </div>
    </form>
  );
}

/* ------------------------------------------------------------------ */
/* The course is shut                                                  */
/* ------------------------------------------------------------------ */

interface OutPayload {
  people: {
    person_id: string; name: string; phone: string | null;
    booking_id: string; course_name: string; starts_at: string; holes: number;
    may_sms: boolean; may_call: boolean; may_email: boolean;
  }[];
  reachable: number;
}

/**
 * Raising one, and sounding the all-clear.
 *
 * Above the frost decision on this tab, not below it, and that ordering
 * is the design. Both are "what is the course doing today"; one of them
 * has minutes in it.
 *
 * The all-clear is a person's act and the button says so. When the club's
 * own window has not elapsed the API refuses with the number of minutes
 * left, and the second button — the one that overrides it — is
 * deliberately not the primary one and deliberately says what it is. A
 * professional who can see blue sky is better informed than a policy;
 * somebody clicking through a countdown because it is in the way is not.
 */
function CourseAlerts({ canAct }: { canAct: boolean }) {
  const [reload, setReload] = useState(0);
  const alerts = useCourseAlerts(20_000, reload);
  const live = useApi<AlertsPayload>(`/v1/golf/alerts?include_cleared=true`, [reload]);
  const out = useApi<OutPayload>('/v1/golf/who-is-out', [reload]);
  const [note, setNote] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState('warning');

  const bump = () => setReload((n) => n + 1);

  const raise = useMutation(async () => {
    const r = await api<{ on_the_course: OutPayload['people'] }>('/v1/golf/alerts', {
      method: 'POST',
      body: { kind: 'lightning', severity, message: message.trim() || null },
    });
    setMessage('');
    // The number is the point. "Eleven out, we have numbers for eight" is
    // what a marshal needs in the first ten seconds.
    setNote(
      r.on_the_course.length === 0
        ? 'Raised. Nobody is checked in on the course.'
        : `Raised. ${r.on_the_course.length} player`
          + `${r.on_the_course.length === 1 ? ' is' : 's are'} out — the list is below.`,
    );
    bump();
  });

  const clear = useMutation(async (v: { id: string; early: boolean }) => {
    const r = await api<{ already_clear: boolean; cleared_early: boolean }>(
      `/v1/golf/alerts/${v.id}/clear`,
      { method: 'POST', body: { before_the_window: v.early, note: null } },
    );
    setNote(
      r.already_clear ? 'That one was already cleared.'
        : r.cleared_early ? 'All-clear sounded early, and recorded as an override.'
          : 'All-clear sounded.',
    );
    bump();
  });

  const open = (live.data?.alerts ?? []).filter((a) => a.is_open);
  const past = (live.data?.alerts ?? []).filter((a) => !a.is_open).slice(0, 5);

  return (
    <>
      <CourseAlertBanner data={alerts} />

      <Card title="Clearing the Course">
        {note && <div className="notice">{note}</div>}
        <ErrorBanner error={live.error ?? raise.error ?? clear.error} />

        {open.length === 0 ? (
          <>
            <p className="muted small">
              Nothing is out. A detector raises one on its own; this is here for the
              morning somebody hears thunder and the box has not caught up.
            </p>
            {canAct && (
              <div className="alert-controls">
                <select value={severity} onChange={(e) => setSeverity(e.target.value)}>
                  <option value="warning">Warning — leave the course</option>
                  <option value="watch">Watch — be ready to come in</option>
                </select>
                <input
                  style={{ flex: 1, minWidth: 220 }}
                  placeholder="What you can see. Shown to every member on the course."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
                <button className="danger" disabled={raise.busy} onClick={() => raise.run()}>
                  Raise it
                </button>
              </div>
            )}
          </>
        ) : (
          <table className="data">
            <thead>
              <tr><th>Raised</th><th>What</th><th>By</th><th>Wait</th><th /></tr>
            </thead>
            <tbody>
              {open.map((a) => (
                <tr key={a.alert_id}>
                  <td className="small">{dateTime(a.detected_at)}</td>
                  <td>
                    {titleCase(a.kind.replace(/_/g, ' '))}
                    <div className="hint">{a.message ?? '—'}</div>
                  </td>
                  <td className="small">
                    {a.raised_by_device ?? a.raised_by_name ?? '—'}
                  </td>
                  <td className="small">
                    {a.minutes_remaining > 0
                      ? `${a.minutes_remaining} min left`
                      : 'elapsed'}
                  </td>
                  <td className="actions">
                    {canAct && (
                      a.minutes_remaining > 0 ? (
                        <button className="secondary small" disabled={clear.busy}
                          onClick={() => clear.run({ id: a.alert_id, early: true })}>
                          Sound it early
                        </button>
                      ) : (
                        <button className="gold small" disabled={clear.busy}
                          onClick={() => clear.run({ id: a.alert_id, early: false })}>
                          Sound the all-clear
                        </button>
                      )
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card
        title="Who Is Out"
        actions={out.data
          ? <span className="muted small">
              {out.data.people.length} checked in · {out.data.reachable} we can reach
            </span>
          : undefined}
      >
        {!out.data || out.data.people.length === 0 ? (
          <Empty>
            Nobody is checked in. Groups that have booked but not arrived are not on
            the course — this list is the one a marshal counts heads against.
          </Empty>
        ) : (
          <table className="data">
            <thead>
              <tr><th>Player</th><th>Off</th><th>Course</th><th>Telephone</th></tr>
            </thead>
            <tbody>
              {out.data.people.map((p) => (
                <tr key={`${p.booking_id}-${p.person_id}`}>
                  <td>{p.name}</td>
                  <td className="mono">{hhmm(p.starts_at)}</td>
                  <td className="small">{p.course_name}</td>
                  <td className="small">
                    {p.phone && (p.may_sms || p.may_call)
                      ? p.phone
                      : <span className="muted">
                          {p.phone ? 'no consent on file' : 'no number on file'}
                        </span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {past.length > 0 && (
        <Card title="Cleared">
          <table className="data">
            <thead>
              <tr><th>Raised</th><th>What</th><th>Cleared</th><th>By</th><th>Note</th></tr>
            </thead>
            <tbody>
              {past.map((a) => (
                <tr key={a.alert_id}>
                  <td className="small">{dateTime(a.detected_at)}</td>
                  <td className="small">{titleCase(a.kind.replace(/_/g, ' '))}</td>
                  <td className="small">{a.cleared_at ? dateTime(a.cleared_at) : '—'}</td>
                  <td className="small">{a.cleared_by_name ?? '—'}</td>
                  <td className="small">{a.cleared_note ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </>
  );
}

function Today({ canDecide, canDelete }: { canDecide: boolean; canDelete: boolean }) {
  const weather = useApi<WeatherPayload>('/v1/golf/weather');
  const days = useApi<CourseDaysPayload>('/v1/golf/course-days');
  const [editing, setEditing] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const body = (v: typeof BLANK_DECISION) => ({
    play_date: v.play_date,
    play_status: v.play_status,
    cart_policy: v.cart_policy,
    first_tee_at: v.play_status === 'delayed' ? v.first_tee_at : null,
    reason: v.play_status === 'open' && v.cart_policy === 'normal' ? null : v.reason || null,
    note: v.note || null,
  });

  const decide = useMutation(async (v: typeof BLANK_DECISION) => {
    const r = await api<{ affected_bookings: number; affected_players: number }>(
      '/v1/golf/course-days', { method: 'PUT', body: body(v) },
    );
    setEditing(null);
    // Not a refusal. Closing the course is exactly what the club meant to
    // do — but somebody has to ring these people, and finding out on the
    // day is worse than finding out now.
    setNote(
      r.affected_bookings > 0
        ? `Saved. ${r.affected_bookings} group${r.affected_bookings === 1 ? '' : 's'} ` +
          `(${r.affected_players} player${r.affected_players === 1 ? '' : 's'}) ` +
          `${r.affected_bookings === 1 ? 'is' : 'are'} booked on ${fmtDate(v.play_date)} ` +
          'and will need telling.'
        : 'Saved.',
    );
    days.reload();
  });

  const remove = useMutation(async (id: string) => {
    await api(`/v1/golf/course-days/${id}`, { method: 'DELETE' });
    setNote('Removed.');
    days.reload();
  });

  const d = days.data;

  return (
    <>
      {weather.loading || days.loading ? <Loading /> : null}
      <ErrorBanner error={weather.error ?? days.error} />
      {note && <div className="notice">{note}</div>}

      {weather.data && <WeatherPanel w={weather.data} />}
      {d && <ProposalPanel p={d.proposal} />}

      {d && canDecide && (
        <Card title={d.decided_today ? 'Revise the day' : 'Record the decision'}>
          {!d.decided_today && d.proposal.has_weather && d.proposal.rules_fired > 0 && (
            <p className="muted small">
              Nothing has been recorded for today. The rules have an opinion above; this is
              where yours goes.
            </p>
          )}
          <DecisionForm
            key={`${d.today}-${d.decided_today}`}
            initial={{
              ...BLANK_DECISION,
              play_date: d.today,
              // Pre-filled from the proposal, which is the whole point of
              // having one: on a frosty morning the professional agrees
              // four times out of five and should not have to retype it.
              play_status: d.proposal.rules_fired > 0 ? d.proposal.play_status : 'open',
              cart_policy: d.proposal.rules_fired > 0 ? d.proposal.cart_policy : 'normal',
              first_tee_at: d.proposal.first_tee_at ? hhmm(d.proposal.first_tee_at) : '',
              reason: d.proposal.reason ?? '',
            }}
            submitLabel={d.decided_today ? 'Update the day' : 'Record it'}
            busy={decide.busy}
            error={decide.error}
            onSubmit={(v) => decide.run(v)}
          />
        </Card>
      )}

      <Card title="The Two Weeks Around Today">
        {!d || d.days.length === 0 ? (
          <Empty>
            Nothing recorded between {d ? fmtDate(d.from) : ''} and {d ? fmtDate(d.to) : ''}.
            An absent day is an ordinary one — the shop does not open the app to say nothing
            happened.
          </Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Day</th>
                <th>What happened</th>
                <th>Reason</th>
                <th>Note</th>
                <th>Recorded by</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {d.days.map((row) => {
                const future = row.play_date > d.today;
                return editing === row.id ? (
                  <tr key={row.id}>
                    <td colSpan={6}>
                      <DecisionForm
                        initial={{
                          play_date: row.play_date,
                          play_status: row.play_status,
                          cart_policy: row.cart_policy,
                          first_tee_at: row.first_tee_at ? hhmm(row.first_tee_at) : '',
                          reason: row.reason ?? '',
                          note: row.note ?? '',
                        }}
                        submitLabel="Save"
                        busy={decide.busy}
                        error={decide.error}
                        onSubmit={(v) => decide.run(v)}
                        onCancel={() => setEditing(null)}
                      />
                    </td>
                  </tr>
                ) : (
                  <tr key={row.id}>
                    <td>
                      {fmtDate(row.play_date)}
                      {row.play_date === d.today && (
                        <div className="hint">today</div>
                      )}
                    </td>
                    <td>
                      <DayStatus status={row.play_status} cart={row.cart_policy} />
                      {row.first_tee_at && (
                        <div className="hint">first tee {hhmm(row.first_tee_at)}</div>
                      )}
                    </td>
                    <td>{row.reason ? titleCase(row.reason) : '—'}</td>
                    <td className="muted small">{row.note ?? '—'}</td>
                    <td className="muted small">
                      {row.decided_by_name ?? '—'}
                      {row.decided_at && <div className="hint">{dateTime(row.decided_at)}</div>}
                    </td>
                    <td>
                      {canDecide && (
                        <Actions>
                          <button className="secondary small" onClick={() => setEditing(row.id)}>
                            Edit
                          </button>
                          {/* Only a day still ahead. What the club did on a
                              morning that has been and gone is the record of
                              a real day, and the server refuses it anyway. */}
                          {canDelete && future && (
                            <DeleteButton
                              confirmLabel="Remove the record"
                              busy={remove.busy}
                              onDelete={() => remove.run(row.id)}
                            />
                          )}
                        </Actions>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <ErrorBanner error={remove.error} />
      </Card>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* The thresholds behind the proposal                                  */
/* ------------------------------------------------------------------ */

const BLANK_RULE = {
  name: '',
  metric: 'dawn_min_temp_c',
  comparator: 'below',
  threshold: '',
  proposes_status: 'open',
  proposes_cart: 'path_only',
  proposes_first_tee_at: '',
  reason: 'frost',
  advice: '',
  from_month: '',
  to_month: '',
  sort_order: '100',
};

function RuleForm({
  initial,
  submitLabel,
  onSubmit,
  onCancel,
  busy,
  error,
}: {
  initial: typeof BLANK_RULE;
  submitLabel: string;
  onSubmit: (v: typeof BLANK_RULE) => void;
  onCancel: () => void;
  busy: boolean;
  error: ReturnType<typeof useMutation>['error'];
}) {
  const [f, setF] = useState(initial);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(f);
      }}
    >
      <ErrorBanner error={error} />
      <div className="grid-2">
        <label className="field">
          <span>Name</span>
          <input required value={f.name} placeholder="Hard frost"
            onChange={(e) => setF({ ...f, name: e.target.value })} />
        </label>
        <label className="field">
          <span>Looks at</span>
          <select value={f.metric} onChange={(e) => setF({ ...f, metric: e.target.value })}>
            {Object.entries(METRIC_LABEL).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Fires when it is</span>
          <select value={f.comparator}
            onChange={(e) => setF({ ...f, comparator: e.target.value })}>
            <option value="below">Below</option>
            <option value="above">Above</option>
          </select>
        </label>
        <label className="field">
          <span>Threshold</span>
          <input required type="number" step="0.1" value={f.threshold}
            onChange={(e) => setF({ ...f, threshold: e.target.value })} />
          <div className="hint">
            Metric: °C, km/h, mm. Readings are stored metric whatever the club reads them in.
          </div>
        </label>
        <label className="field">
          <span>Then suggest play is</span>
          <select value={f.proposes_status}
            onChange={(e) => setF({ ...f, proposes_status: e.target.value })}>
            <option value="open">Open</option>
            <option value="delayed">Delayed</option>
            <option value="closed">Closed</option>
          </select>
        </label>
        <label className="field">
          <span>and carts are</span>
          <select value={f.proposes_cart}
            onChange={(e) => setF({ ...f, proposes_cart: e.target.value })}>
            <option value="normal">Normal</option>
            <option value="path_only">On the path</option>
            <option value="none">In</option>
          </select>
        </label>
        {f.proposes_status === 'delayed' && (
          <label className="field">
            <span>Suggested first tee</span>
            <input required type="time" value={f.proposes_first_tee_at}
              onChange={(e) => setF({ ...f, proposes_first_tee_at: e.target.value })} />
          </label>
        )}
        <label className="field">
          <span>Reason</span>
          <select value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })}>
            {REASONS.map((r) => <option key={r} value={r}>{titleCase(r)}</option>)}
          </select>
        </label>
        <label className="field">
          <span>From month</span>
          <select value={f.from_month}
            onChange={(e) => setF({ ...f, from_month: e.target.value })}>
            <option value="">All year</option>
            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
        </label>
        <label className="field">
          <span>To month</span>
          <select value={f.to_month} onChange={(e) => setF({ ...f, to_month: e.target.value })}>
            <option value="">All year</option>
            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
          <div className="hint">
            A window may wrap the new year — October to April is a winter, not a mistake.
          </div>
        </label>
      </div>
      <label className="field">
        <span>Advice</span>
        <input value={f.advice}
          placeholder="Walk 4 and 13 before you release — the hollows hold it longest."
          onChange={(e) => setF({ ...f, advice: e.target.value })} />
        <div className="hint">
          Shown whenever this rule fires. The local knowledge no threshold contains.
        </div>
      </label>
      <div className="actions">
        <button className="gold" disabled={busy}>{busy ? 'Saving…' : submitLabel}</button>
        <button type="button" className="secondary" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

function ConditionRules({ canEdit }: { canEdit: boolean }) {
  const q = useApi<{ rules: ConditionRule[] }>('/v1/golf/condition-rules');
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);

  const body = (v: typeof BLANK_RULE) => ({
    name: v.name,
    metric: v.metric,
    comparator: v.comparator,
    threshold: Number(v.threshold),
    proposes_status: v.proposes_status,
    proposes_cart: v.proposes_cart,
    proposes_first_tee_at: v.proposes_status === 'delayed' ? v.proposes_first_tee_at : null,
    reason: v.reason,
    advice: v.advice || null,
    from_month: v.from_month ? Number(v.from_month) : null,
    to_month: v.to_month ? Number(v.to_month) : null,
    sort_order: Number(v.sort_order),
  });

  const create = useMutation(async (v: typeof BLANK_RULE) => {
    await api('/v1/golf/condition-rules', { method: 'POST', body: body(v) });
    setAdding(false);
    q.reload();
  });

  const update = useMutation(async (id: string, v: typeof BLANK_RULE) => {
    await api(`/v1/golf/condition-rules/${id}`, { method: 'PATCH', body: body(v) });
    setEditing(null);
    q.reload();
  });

  const toggle = useMutation(async (id: string, active: boolean) => {
    await api(`/v1/golf/condition-rules/${id}`, {
      method: 'PATCH', body: { is_active: active },
    });
    q.reload();
  });

  const remove = useMutation(async (id: string) => {
    await api(`/v1/golf/condition-rules/${id}`, { method: 'DELETE' });
    q.reload();
  });

  const rules = q.data?.rules ?? [];

  return (
    <Card
      title="The Thresholds Behind All That"
      actions={
        canEdit && !adding && (
          <button className="gold small" onClick={() => setAdding(true)}>Add a rule</button>
        )
      }
    >
      <p className="muted small">
        These are the club's opinions, not the software's. "Frost is below 2°C at dawn" depends
        on the grass, the drainage and the person who has walked it — change the number and the
        proposal changes with it. Nothing already decided is affected: a rule is not the reason
        anything happened, only the reason something was suggested.
      </p>

      {adding && (
        <RuleForm initial={BLANK_RULE} submitLabel="Add the rule" busy={create.busy}
          error={create.error} onSubmit={(v) => create.run(v)}
          onCancel={() => setAdding(false)} />
      )}

      <ErrorBanner error={q.error ?? update.error ?? remove.error ?? toggle.error} />
      {q.loading ? <Loading /> : rules.length === 0 ? (
        <Empty>No rules. Nothing will ever be proposed.</Empty>
      ) : (
        <table className="data">
          <thead>
            <tr>
              <th>Rule</th>
              <th>When</th>
              <th>Season</th>
              <th>Suggests</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rules.map((r) => editing === r.id ? (
              <tr key={r.id}>
                <td colSpan={5}>
                  <RuleForm
                    initial={{
                      name: r.name,
                      metric: r.metric,
                      comparator: r.comparator,
                      threshold: String(r.threshold),
                      proposes_status: r.proposes_status,
                      proposes_cart: r.proposes_cart,
                      proposes_first_tee_at: r.proposes_first_tee_at
                        ? hhmm(r.proposes_first_tee_at) : '',
                      reason: r.reason,
                      advice: r.advice ?? '',
                      from_month: r.from_month ? String(r.from_month) : '',
                      to_month: r.to_month ? String(r.to_month) : '',
                      sort_order: String(r.sort_order),
                    }}
                    submitLabel="Save"
                    busy={update.busy}
                    error={update.error}
                    onSubmit={(v) => update.run(r.id, v)}
                    onCancel={() => setEditing(null)}
                  />
                </td>
              </tr>
            ) : (
              <tr key={r.id} style={r.is_active ? undefined : { opacity: 0.55 }}>
                <td>
                  <strong>{r.name}</strong>
                  {!r.is_active && <div className="hint">off</div>}
                  {r.advice && <div className="hint">{r.advice}</div>}
                </td>
                <td>
                  {METRIC_LABEL[r.metric] ?? r.metric}{' '}
                  <span className="mono">
                    {r.comparator === 'below' ? '<' : '>'} {r.threshold}
                  </span>
                </td>
                <td className="muted small">
                  {r.from_month && r.to_month
                    ? `${MONTHS[r.from_month - 1]}–${MONTHS[r.to_month - 1]}`
                    : 'All year'}
                </td>
                <td>
                  <DayStatus status={r.proposes_status} cart={r.proposes_cart} />
                  {r.proposes_first_tee_at && (
                    <div className="hint">till {hhmm(r.proposes_first_tee_at)}</div>
                  )}
                </td>
                <td>
                  {canEdit && (
                    <Actions>
                      <button className="secondary small" onClick={() => setEditing(r.id)}>
                        Edit
                      </button>
                      {/* Turning a rule off is the gentler answer and is
                          offered first — a deleted rule takes its advice
                          text with it, and that is the part nobody can
                          rewrite from memory. */}
                      <button className="secondary small" disabled={toggle.busy}
                        onClick={() => toggle.run(r.id, !r.is_active)}>
                        {r.is_active ? 'Turn off' : 'Turn on'}
                      </button>
                      <DeleteButton confirmLabel="Delete the rule" busy={remove.busy}
                        onDelete={() => remove.run(r.id)} />
                    </Actions>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Club holidays                                                       */
/*                                                                     */
/* Not closures. A holiday is a weekday the club prices and polices as */
/* a weekend, which is the opposite of a day the course is shut — the  */
/* Fourth of July is the busiest morning of the golf year. The two get */
/* confused often enough that the screen says so out loud.             */
/* ------------------------------------------------------------------ */

interface Holiday {
  id: string;
  holiday_on: string;
  name: string;
  is_past: boolean;
  weekday: string;
  changes_the_rate: boolean;
  slots: number;
}

function Holidays({ canEdit }: { canEdit: boolean }) {
  const [includePast, setIncludePast] = useState(false);
  const q = useApi<{ holidays: Holiday[] }>(
    `/v1/golf/holidays?include_past=${includePast}`, [includePast],
  );
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({ holiday_on: '', name: '' });
  const [note, setNote] = useState<string | null>(null);

  const create = useMutation(async () => {
    const r = await api<{ bookings_already_taken: number }>('/v1/golf/holidays', {
      method: 'POST', body: f,
    });
    setAdding(false);
    setF({ holiday_on: '', name: '' });
    // Nobody is charged until check-in, so this is not a refusal. It is
    // still the sentence that prevents an argument at the desk.
    setNote(
      r.bookings_already_taken > 0
        ? `Added. ${r.bookings_already_taken} group${r.bookings_already_taken === 1 ? '' : 's'} ` +
          'already booked that day will now be quoted the weekend rate.'
        : 'Added.',
    );
    q.reload();
  });

  const remove = useMutation(async (id: string) => {
    await api(`/v1/golf/holidays/${id}`, { method: 'DELETE' });
    setNote('Removed.');
    q.reload();
  });

  const holidays = q.data?.holidays ?? [];

  return (
    <Card
      title="Club Holidays"
      actions={
        <div className="actions">
          <button className="secondary small" onClick={() => setIncludePast(!includePast)}>
            {includePast ? 'Coming up only' : 'Show past ones'}
          </button>
          {canEdit && !adding && (
            <button className="gold small" onClick={() => setAdding(true)}>Add a holiday</button>
          )}
        </div>
      }
    >
      <p className="muted small">
        A day the club prices and polices as a weekend. Not a closure — for that, close the day
        on <strong>Today on the course</strong>. A rate card that only knows Saturday and Sunday
        charges Monday's rate on the Fourth of July.
      </p>

      {note && <div className="notice">{note}</div>}

      {adding && (
        <form onSubmit={(e) => { e.preventDefault(); create.run(); }}>
          <ErrorBanner error={create.error} />
          <div className="grid-2">
            <label className="field">
              <span>Date</span>
              <input required type="date" value={f.holiday_on}
                onChange={(e) => setF({ ...f, holiday_on: e.target.value })} />
            </label>
            <label className="field">
              <span>Name</span>
              <input required value={f.name} placeholder="Independence Day"
                onChange={(e) => setF({ ...f, name: e.target.value })} />
            </label>
          </div>
          <div className="actions">
            <button className="gold" disabled={create.busy}>
              {create.busy ? 'Saving…' : 'Add it'}
            </button>
            <button type="button" className="secondary" onClick={() => setAdding(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}

      <ErrorBanner error={q.error ?? remove.error} />
      {q.loading ? <Loading /> : holidays.length === 0 ? (
        <Empty>
          {includePast ? 'No holidays at all.' : 'None coming up.'}
        </Empty>
      ) : (
        <table className="data">
          <thead>
            <tr>
              <th>Date</th>
              <th>Name</th>
              <th>Effect</th>
              <th className="num">Slots</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {holidays.map((h) => (
              <tr key={h.id} style={h.is_past ? { opacity: 0.6 } : undefined}>
                <td>
                  {fmtDate(h.holiday_on)}
                  <div className="hint">{h.weekday}</div>
                </td>
                <td>{h.name}</td>
                <td>
                  {h.changes_the_rate
                    ? <Status tone="good">Charges the weekend rate</Status>
                    // Saying this rather than showing an identical row is
                    // the difference between a shop that trusts the screen
                    // and one that checks the rate card by hand.
                    : <span className="muted small">Already a weekend — changes nothing</span>}
                </td>
                <td className="num">{h.slots || '—'}</td>
                <td>
                  {canEdit && !h.is_past && (
                    <Actions>
                      <DeleteButton confirmLabel="Remove the holiday" busy={remove.busy}
                        onDelete={() => remove.run(h.id)} />
                    </Actions>
                  )}
                  {h.is_past && (
                    <span className="muted small">
                      priced somebody's round
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Events                                                              */
/*                                                                     */
/* Drawn around the one thing a professional cares about on an event   */
/* screen: whether the morning is actually off the members yet. An     */
/* event that is planned looks different from one that is holding the  */
/* sheet, because confusing the two is how a club discovers on the     */
/* Friday that the outing was never blocked.                           */
/*                                                                     */
/* The number the board asks about — how many member places the event  */
/* costs — is shown wherever the event is, not buried in a report.     */
/* ------------------------------------------------------------------ */

interface GolfEvent {
  id: string;
  course_id: string;
  course_name: string;
  name: string;
  kind: string;
  play_date: string;
  start_style: string;
  window_from: string;
  window_to: string;
  shotgun_at: string | null;
  holes: number;
  max_players: number | null;
  status: string;
  notes: string | null;
  field_size: number;
  drawn: number;
  slots_held: number;
  member_places_given_up: number;

  // 0116. Absent on the list endpoint until it is asked for; every
  // consumer treats undefined as "not set", which is also what null
  // means here.
  members_may_enter?: boolean;
  entries_open_on?: string | null;
  entries_close_on?: string | null;
  entry_fee_minor?: string | number | null;
  entry_fee_per?: string;
  guest_fee_minor?: string | number | null;
  revenue_category?: string | null;
  fee_includes?: string | null;
  players_per_entry?: number | null;
}

interface FieldPlayer {
  id: string;
  person_id: string | null;
  membership_id: string | null;
  player_kind: string;
  starting_hole: number | null;
  group_no: number | null;
  status: string;
  note: string | null;
  name: string;
  membership_number: string | null;
}

const EVENT_KINDS = ['play_day', 'tournament', 'league', 'outing', 'clinic', 'other'];

const KIND_LABEL: Record<string, string> = {
  play_day: 'Play day',
  tournament: 'Tournament',
  league: 'League',
  outing: 'Outing',
  clinic: 'Clinic',
  other: 'Other',
};

/**
 * Whether the morning is actually off the members.
 *
 * Deliberately not the same words as the status column. "Scheduled"
 * describes the event; "holding 46 slots" describes the sheet, and the
 * sheet is the thing that stops a member booking.
 */
function SheetState({ e }: { e: GolfEvent }) {
  if (e.status === 'cancelled') return <Status tone="neutral">Cancelled</Status>;
  if (e.status === 'played') return <Status tone="neutral">Played</Status>;
  if (e.slots_held > 0) {
    return (
      <Status tone="good">
        Holding {e.slots_held} slot{e.slots_held === 1 ? '' : 's'}
      </Status>
    );
  }
  // The dangerous state, and the one worth colouring: the club believes
  // it has an event and the members can still book the morning.
  return <Status tone="warning">Sheet not taken</Status>;
}

const BLANK_EVENT = {
  name: '',
  kind: 'play_day',
  play_date: '',
  start_style: 'tee_times',
  window_from: '07:00',
  window_to: '13:00',
  shotgun_at: '08:00',
  holes: '18',
  max_players: '',
  notes: '',
  // 0116. Empty is a real answer for all of these: most of the calendar
  // is play days nobody signs up for and nobody pays for.
  members_may_enter: false,
  entries_open_on: '',
  entries_close_on: '',
  entry_fee: '',
  entry_fee_per: 'entry',
  guest_fee: '',
  revenue_category: 'tournament_fee',
  fee_includes: '',
  players_per_entry: '',
};

function EventForm({
  initial,
  submitLabel,
  onSubmit,
  onCancel,
  busy,
  error,
}: {
  initial: typeof BLANK_EVENT;
  submitLabel: string;
  onSubmit: (v: typeof BLANK_EVENT) => void;
  onCancel: () => void;
  busy: boolean;
  error: ReturnType<typeof useMutation>['error'];
}) {
  const [f, setF] = useState(initial);
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(f); }}>
      <ErrorBanner error={error} />
      <div className="grid-2">
        <label className="field">
          <span>Name</span>
          <input required value={f.name} placeholder="Member-Guest"
            onChange={(e) => setF({ ...f, name: e.target.value })} />
        </label>
        <label className="field">
          <span>Kind</span>
          <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}>
            {EVENT_KINDS.map((k) => (
              <option key={k} value={k}>{KIND_LABEL[k]}</option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Day</span>
          <input required type="date" value={f.play_date}
            onChange={(e) => setF({ ...f, play_date: e.target.value })} />
        </label>
        <label className="field">
          <span>Start</span>
          <select value={f.start_style}
            onChange={(e) => setF({ ...f, start_style: e.target.value })}>
            <option value="tee_times">Tee times</option>
            <option value="shotgun">Shotgun</option>
          </select>
        </label>
        <label className="field">
          <span>Window from</span>
          <input required type="time" value={f.window_from}
            onChange={(e) => setF({ ...f, window_from: e.target.value })} />
          <div className="hint">The slots taken off the members.</div>
        </label>
        <label className="field">
          <span>Window to</span>
          <input required type="time" value={f.window_to}
            onChange={(e) => setF({ ...f, window_to: e.target.value })} />
        </label>
        {f.start_style === 'shotgun' && (
          <label className="field">
            <span>Shotgun at</span>
            <input required type="time" value={f.shotgun_at}
              onChange={(e) => setF({ ...f, shotgun_at: e.target.value })} />
            <div className="hint">Everybody goes off at once, on a hole each.</div>
          </label>
        )}
        <label className="field">
          <span>Holes</span>
          <select value={f.holes} onChange={(e) => setF({ ...f, holes: e.target.value })}>
            <option value="18">18</option>
            <option value="9">9</option>
          </select>
        </label>
        <label className="field">
          <span>Field limit</span>
          <input type="number" min={1} max={400} value={f.max_players}
            placeholder="not decided"
            onChange={(e) => setF({ ...f, max_players: e.target.value })} />
          <div className="hint">Leave empty until the club has settled on a number.</div>
        </label>
      </div>
      <label className="field">
        <span>Notes</span>
        <input value={f.notes} placeholder="Two-day, better ball."
          onChange={(e) => setF({ ...f, notes: e.target.value })} />
      </label>

      {/* Everything below is off by default and folds away, because most
          of the calendar is play days nobody signs up for. An outing for
          a company is not a thing members enter, and a form that offers
          it by default is a form that takes bad entries. */}
      <label className="field">
        <span>
          <input type="checkbox" checked={f.members_may_enter}
            onChange={(e) => setF({ ...f, members_may_enter: e.target.checked })} />
          {' '}Members can sign themselves up
        </span>
      </label>

      {f.members_may_enter && (
        <div className="grid-2">
          <label className="field">
            <span>Entries open</span>
            <input type="date" value={f.entries_open_on}
              onChange={(e) => setF({ ...f, entries_open_on: e.target.value })} />
            <div className="hint">Empty means they are open now.</div>
          </label>
          <label className="field">
            <span>Entries close</span>
            <input type="date" value={f.entries_close_on}
              onChange={(e) => setF({ ...f, entries_close_on: e.target.value })} />
            <div className="hint">The shop can still add a pair after this.</div>
          </label>
          <label className="field">
            <span>Players per entry</span>
            <input type="number" min={1} max={8} value={f.players_per_entry}
              placeholder="any"
              onChange={(e) => setF({ ...f, players_per_entry: e.target.value })} />
            <div className="hint">2 for a member-guest, 4 for a scramble.</div>
          </label>
          <label className="field">
            <span>Entry fee</span>
            <input value={f.entry_fee} placeholder="340.00"
              onChange={(e) => setF({ ...f, entry_fee: e.target.value })} />
          </label>
          <label className="field">
            <span>Charged</span>
            <select value={f.entry_fee_per}
              onChange={(e) => setF({ ...f, entry_fee_per: e.target.value })}>
              <option value="entry">Per entry</option>
              <option value="player">Per player</option>
            </select>
            {/* The mistake worth pre-empting: wrong here is a bill that
                is double or half, on every account in the field. */}
            <div className="hint">
              A member-guest is per pair. A championship is per player.
            </div>
          </label>
          <label className="field">
            <span>Guest fee, on top</span>
            <input value={f.guest_fee} placeholder="none"
              onChange={(e) => setF({ ...f, guest_fee: e.target.value })} />
            <div className="hint">Leave empty where the entry fee covers them.</div>
          </label>
        </div>
      )}

      {f.members_may_enter && (
        <label className="field">
          <span>What the fee covers</span>
          <input value={f.fee_includes} placeholder="Cart, range balls and lunch."
            onChange={(e) => setF({ ...f, fee_includes: e.target.value })} />
          <div className="hint">
            Shown to members beside the price. &ldquo;Is the cart included&rdquo; is the
            question the shop answers forty times a week in June.
          </div>
        </label>
      )}

      <div className="actions">
        <button className="gold" disabled={busy}>{busy ? 'Saving…' : submitLabel}</button>
        <button type="button" className="secondary" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}


/* ------------------------------------------------------------------ */
/* Who has entered, and who is waiting                                 */
/* ------------------------------------------------------------------ */

interface EntryRow {
  entry_id: string;
  sequence_no: number;
  team_name: string | null;
  status: string;
  entered_by_name: string | null;
  players: number;
  player_names: string | null;
  amount_minor: string | number | null;
  is_billed: boolean;
  withdrawn_reason: string | null;
}

interface WaitingRow {
  entry_id: string;
  sequence_no: number;
  team_name: string | null;
  entered_name: string | null;
  players: number;
  would_fit: boolean;
}

/**
 * The entries on one event.
 *
 * Two things this screen insists on.
 *
 * The waiting list is in the order people applied and says whether each
 * would fit TODAY, because a four that does not go into a gap of two is
 * still on the list and the shop should see that rather than ring them
 * to find out.
 *
 * And promotion is a button somebody presses. It commits a member to a
 * bill, and a pair that has been waiting three weeks may have made other
 * plans — software that entered them automatically would be billing them
 * for a Saturday they did not know they had.
 */
function Entries({
  eventId, event, canManage, onChanged,
}: {
  eventId: string;
  event: GolfEvent;
  canManage: boolean;
  onChanged: () => void;
}) {
  const { currency } = useSession();
  const [reload, setReload] = useState(0);
  const q = useApi<{
    entries: EntryRow[]; waiting: WaitingRow[];
    in_field: number; players_in_field: number; unbilled: number;
  }>(`/v1/golf/events/${eventId}/entries`, [eventId, reload]);
  const [note, setNote] = useState<string | null>(null);

  const bump = () => { setReload((n) => n + 1); onChanged(); };

  const promote = useMutation(async (id: string) => {
    await api(`/v1/golf/events/${eventId}/entries/${id}/promote`, { method: 'POST' });
    setNote('Moved into the field. They are not billed until entries close.');
    bump();
  });

  const withdraw = useMutation(async (id: string) => {
    await api(`/v1/golf/events/${eventId}/entries/${id}`, {
      method: 'DELETE', body: { reason: 'Withdrawn at the shop.' },
    });
    setNote('Withdrawn.');
    bump();
  });

  const close = useMutation(async () => {
    const r = await api<{
      entries_in: number; posted: number; already_posted: number;
      could_not_post: number; trouble: string | null;
    }>(`/v1/golf/events/${eventId}/close-entries`, {
      method: 'POST', body: { post_fees: true },
    });
    setNote(
      r.could_not_post > 0
        // Named, not counted. "One could not be billed" sends somebody
        // looking; the name tells them where to go.
        ? `Entries closed. ${r.posted} billed, ${r.already_posted} already were, `
          + `${r.could_not_post} could not be — ${r.trouble}`
        : `Entries closed. ${r.posted} billed`
          + (r.already_posted ? `, ${r.already_posted} already were.` : '.'),
    );
    bump();
  });

  const d = q.data;
  const inField = (d?.entries ?? []).filter((e) => e.status === 'entered');
  const gone = (d?.entries ?? []).filter((e) => e.status === 'withdrawn');

  return (
    <Card
      title="Entries"
      actions={canManage && (d?.unbilled ?? 0) > 0
        ? <button className="gold small" disabled={close.busy} onClick={() => close.run()}>
            Close entries &amp; post the fees
          </button>
        : undefined}
    >
      {note && <div className="notice">{note}</div>}
      <ErrorBanner error={q.error ?? promote.error ?? withdraw.error ?? close.error} />

      <div className="tiles">
        <Tile label="In the field" value={d?.in_field ?? 0}
          note={`${d?.players_in_field ?? 0} player${
            (d?.players_in_field ?? 0) === 1 ? '' : 's'}`} />
        <Tile label="Waiting" value={d?.waiting.length ?? 0}
          note={(d?.waiting ?? []).some((w) => w.would_fit)
            ? 'somebody would fit now' : 'no room'} />
        <Tile label="Not yet billed" value={d?.unbilled ?? 0}
          note={event.entry_fee_minor
            ? <>
                <Money minor={event.entry_fee_minor} currency={currency} />
                {' '}per {event.entry_fee_per === 'player' ? 'player' : 'entry'}
              </>
            : 'no fee set'} />
      </div>

      {inField.length === 0 ? (
        <Empty>
          Nobody has entered yet. Members sign up from their own Golf page; the shop can
          enter anybody, including after entries close.
        </Empty>
      ) : (
        <table className="data">
          <thead>
            <tr>
              <th className="num">#</th><th>Entry</th><th>Playing</th>
              <th className="num">Fee</th><th /></tr>
          </thead>
          <tbody>
            {inField.map((en) => (
              <tr key={en.entry_id}>
                <td className="num mono">{en.sequence_no}</td>
                <td>
                  {en.team_name ?? en.entered_by_name ?? '—'}
                  {en.team_name && en.entered_by_name && (
                    <div className="hint">{en.entered_by_name}</div>
                  )}
                </td>
                <td className="small">{en.player_names ?? `${en.players} player(s)`}</td>
                <td className="num">
                  {en.is_billed
                    ? <Money minor={en.amount_minor} currency={currency} />
                    : <span className="muted">not yet</span>}
                </td>
                <td className="actions">
                  {canManage && !en.is_billed && (
                    <DeleteButton label="Withdraw" confirmLabel="Withdraw it"
                      busy={withdraw.busy}
                      onDelete={() => withdraw.run(en.entry_id)} />
                  )}
                  {en.is_billed && <span className="muted small">billed</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {(d?.waiting.length ?? 0) > 0 && (
        <>
          <h3 className="small" style={{ marginTop: 18 }}>Waiting list</h3>
          <table className="data">
            <thead>
              <tr><th className="num">#</th><th>Entry</th><th className="num">Players</th>
                <th /></tr>
            </thead>
            <tbody>
              {(d?.waiting ?? []).map((w) => (
                <tr key={w.entry_id}>
                  <td className="num mono">{w.sequence_no}</td>
                  <td>{w.team_name ?? w.entered_name ?? '—'}</td>
                  <td className="num">{w.players}</td>
                  <td className="actions">
                    {canManage && w.would_fit && (
                      <button className="gold small" disabled={promote.busy}
                        onClick={() => promote.run(w.entry_id)}>
                        Ring them and move up
                      </button>
                    )}
                    {!w.would_fit && (
                      <span className="muted small">
                        no room for {w.players} yet
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {gone.length > 0 && (
        <p className="hint" style={{ marginTop: 10 }}>
          {gone.length} withdrawn — {gone.map((g) => g.team_name ?? '—').join(', ')}.
        </p>
      )}
    </Card>
  );
}


/* ------------------------------------------------------------------ */
/* The result                                                          */
/* ------------------------------------------------------------------ */

const COMPETITION_FORMATS = ['net', 'gross', 'stableford', 'best_ball'];

/**
 * The competitions run off one event, and their leaderboards.
 *
 * The form offers an allowance because a club runs a member-guest off
 * ninety per cent and a championship off scratch, and one that cannot
 * say so keeps a second sheet — which is the thing this whole module
 * exists to replace.
 *
 * Nothing here takes a score. Every number on a board below is worked
 * out from the cards on the Cards & handicaps tab, so a card corrected
 * on Tuesday moves the result on Tuesday.
 */
function Competitions({
  eventId, canManage,
}: { eventId: string; canManage: boolean }) {
  const [reload, setReload] = useState(0);
  const q = useApi<{ competitions: CompetitionRow[] }>(
    `/v1/golf/events/${eventId}/competitions`, [eventId, reload]);
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [live, setLive] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [f, setF] = useState({ name: '', format: 'net', allowance: '100', division: '',
    places_paid: '' });

  const bump = () => setReload((n) => n + 1);

  const create = useMutation(async () => {
    await api(`/v1/golf/events/${eventId}/competitions`, {
      method: 'POST',
      body: {
        name: f.name.trim(),
        format: f.format,
        handicap_allowance: Number(f.allowance) / 100,
        division: f.division.trim() || null,
        places_paid: f.places_paid ? Number(f.places_paid) : null,
      },
    });
    setAdding(false);
    setF({ name: '', format: 'net', allowance: '100', division: '', places_paid: '' });
    bump();
  });

  const declare = useMutation(async (v: { id: string; status: string }) => {
    await api(`/v1/golf/competitions/${v.id}`, {
      method: 'PATCH', body: { status: v.status },
    });
    setNote(v.status === 'final'
      ? 'Declared. Reopen it if a card needs correcting — the change will be visible.'
      : 'Reopened.');
    bump();
  });

  const comps = q.data?.competitions ?? [];

  return (
    <>
      <Card
        title="The Result"
        actions={canManage && !adding
          ? <button className="gold small" onClick={() => setAdding(true)}>
              Add a competition
            </button>
          : undefined}
      >
        {note && <div className="notice">{note}</div>}
        <ErrorBanner error={q.error ?? create.error ?? declare.error} />

        <p className="muted small">
          Worked out from the cards, never typed. One morning can carry several — the
          gross, the net, and the ladies&apos; net off the same field.
        </p>

        {adding && (
          <form className="form-rows" style={{ maxWidth: 560 }}
            onSubmit={(e) => { e.preventDefault(); create.run(); }}>
            <div className="grid-2">
              <label className="field">
                <span>Name</span>
                <input required value={f.name} placeholder="Net"
                  onChange={(e) => setF({ ...f, name: e.target.value })} />
              </label>
              <label className="field">
                <span>Format</span>
                <select value={f.format}
                  onChange={(e) => setF({ ...f, format: e.target.value })}>
                  {COMPETITION_FORMATS.map((k) => (
                    <option key={k} value={k}>{FORMAT_LABEL[k] ?? k}</option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>Handicap allowance</span>
                <input type="number" min={0} max={150} value={f.allowance}
                  onChange={(e) => setF({ ...f, allowance: e.target.value })} />
                <div className="hint">
                  Per cent. 100 for the medal, 90 for a member-guest, 0 for a
                  championship off scratch.
                </div>
              </label>
              <label className="field">
                <span>Division</span>
                <input value={f.division} placeholder="everybody"
                  onChange={(e) => setF({ ...f, division: e.target.value })} />
              </label>
              <label className="field">
                <span>Places paid</span>
                <input type="number" min={1} max={50} value={f.places_paid}
                  placeholder="none"
                  onChange={(e) => setF({ ...f, places_paid: e.target.value })} />
              </label>
            </div>
            <div className="actions">
              <button className="gold" type="submit" disabled={create.busy}>Add it</button>
              <button className="secondary" type="button"
                onClick={() => setAdding(false)}>Cancel</button>
            </div>
          </form>
        )}

        {comps.length === 0 && !adding ? (
          <Empty>
            No competition on this one. Plenty of events are not competitions — a
            league night and an outing are days on the course, not results.
          </Empty>
        ) : comps.length > 0 ? (
          <table className="data">
            <thead>
              <tr>
                <th>Competition</th><th>Format</th>
                <th className="num">Cards in</th><th>State</th><th /></tr>
            </thead>
            <tbody>
              {comps.map((c) => (
                <tr key={c.competition_id}>
                  <td>
                    {c.name}
                    {c.division && <div className="hint">{c.division}</div>}
                  </td>
                  <td className="small">
                    {FORMAT_LABEL[c.format] ?? c.format}
                    {Number(c.handicap_allowance) !== 1 && (
                      <div className="hint">
                        {Math.round(Number(c.handicap_allowance) * 100)}% handicap
                      </div>
                    )}
                  </td>
                  <td className="num">
                    {c.cards_in}
                    {c.no_returns > 0 && (
                      <div className="hint">{c.no_returns} still out</div>
                    )}
                  </td>
                  <td>
                    {c.status === 'final'
                      ? <Status tone="good">Declared</Status>
                      : c.status === 'abandoned'
                        ? <span className="muted small">Abandoned</span>
                        : <Status tone="warning">Open</Status>}
                  </td>
                  <td className="actions">
                    <button className="secondary small"
                      onClick={() => setOpen(
                        open === c.competition_id ? null : c.competition_id)}>
                      {open === c.competition_id ? 'Hide' : 'Leaderboard'}
                    </button>
                    {/* Two buttons rather than one board with a switch,
                        because they answer different questions: the live
                        one is ordered on how far under par everybody is
                        for what they have played, and the leaderboard on
                        the total. A single board that changed its mind
                        would be the thing 0120 argues against. */}
                    <button className="secondary small"
                      onClick={() => setLive(
                        live === c.competition_id ? null : c.competition_id)}>
                      {live === c.competition_id ? 'Hide' : 'Live'}
                    </button>
                    {canManage && c.status !== 'final' && c.cards_in > 0 && (
                      <button className="gold small" disabled={declare.busy}
                        onClick={() => declare.run(
                          { id: c.competition_id, status: 'final' })}>
                        Declare it
                      </button>
                    )}
                    {canManage && c.status === 'final' && (
                      <button className="secondary small" disabled={declare.busy}
                        onClick={() => declare.run(
                          { id: c.competition_id, status: 'open' })}>
                        Reopen
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </Card>

      {live && <LiveBoard competitionId={live} />}
      {open && <Leaderboard competitionId={open} reloadKey={reload} />}
    </>
  );
}

/**
 * One event opened up: the field, the draw, and the sheet controls.
 *
 * Nested inside the list rather than on its own route because an event is
 * read alongside its neighbours — "is the Tuesday league in the way of
 * the outing?" is the question, and answering it on two screens is worse.
 */
function EventDetail({
  eventId,
  canManage,
  canEnter,
  onChanged,
  onClose,
}: {
  eventId: string;
  canManage: boolean;
  canEnter: boolean;
  onChanged: () => void;
  onClose: () => void;
}) {
  const q = useApi<{ event: GolfEvent; field: FieldPlayer[];
    sheet: { slots: number; first_slot: string | null; last_slot: string | null } }>(
    `/v1/golf/events/${eventId}`, [eventId]);
  const [note, setNote] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [entry, setEntry] = useState({ display_name: '', player_kind: 'outside' });
  const [perGroup, setPerGroup] = useState('4');

  const reload = () => { q.reload(); onChanged(); };

  const claim = useMutation(async () => {
    const r = await api<{ slots_taken: number; member_places_given_up: number }>(
      `/v1/golf/events/${eventId}/claim`, { method: 'POST' });
    setNote(
      r.slots_taken === 0
        ? 'Nothing to take — the sheet is not laid out for that day yet. Open it first, ' +
          'then take the window again.'
        : `Taken. ${r.slots_taken} slot(s) are off the members, which is ` +
          `${r.member_places_given_up} places they can no longer book.`,
    );
    reload();
  });

  const release = useMutation(async () => {
    const r = await api<{ slots_handed_back: number }>(
      `/v1/golf/events/${eventId}/release`, { method: 'POST' });
    setNote(
      `Handed back. ${r.slots_handed_back} slot(s) returned to whatever they were before — ` +
      'anything blocked for frost or maintenance stays blocked.',
    );
    reload();
  });

  const draw = useMutation(async () => {
    const r = await api<{ groups: number }>(`/v1/golf/events/${eventId}/draw`, {
      method: 'POST', body: { per_group: Number(perGroup) },
    });
    setNote(`Drawn into ${r.groups} group(s), starting on holes 1 to ${r.groups}.`);
    reload();
  });

  const addPlayer = useMutation(async () => {
    await api(`/v1/golf/events/${eventId}/players`, { method: 'POST', body: entry });
    setEntry({ display_name: '', player_kind: 'outside' });
    setAdding(false);
    reload();
  });

  const setStatus = useMutation(async (status: string) => {
    const r = await api<{ slots_handed_back: number }>(`/v1/golf/events/${eventId}`, {
      method: 'PATCH', body: { status },
    });
    setNote(
      status === 'cancelled' && r.slots_handed_back > 0
        ? `Cancelled. ${r.slots_handed_back} slot(s) went back to the members.`
        : 'Saved.',
    );
    reload();
  });

  const withdraw = useMutation(async (playerId: string) => {
    await api(`/v1/golf/events/${eventId}/players/${playerId}`, {
      method: 'PATCH', body: { status: 'withdrawn' },
    });
    reload();
  });

  const removePlayer = useMutation(async (playerId: string) => {
    await api(`/v1/golf/events/${eventId}/players/${playerId}`, { method: 'DELETE' });
    reload();
  });

  if (q.loading) return <Card><Loading /></Card>;
  if (!q.data) return <Card><ErrorBanner error={q.error} /></Card>;

  const e = q.data.event;
  const live = e.status !== 'cancelled' && e.status !== 'played';

  return (
    <Card
      title={e.name}
      actions={<button className="secondary small" onClick={onClose}>Close</button>}
    >
      <div className="tiles">
        <Tile label="Day" value={fmtDate(e.play_date)}
          note={e.start_style === 'shotgun'
            ? `Shotgun at ${hhmm(e.shotgun_at ?? '')}`
            : `Tee times ${hhmm(e.window_from)}–${hhmm(e.window_to)}`} />
        <Tile label="Field" value={`${e.field_size}${e.max_players ? ` / ${e.max_players}` : ''}`}
          note={e.max_players ? undefined : 'no limit set'} />
        <Tile label="Sheet" value={<SheetState e={e} />}
          note={q.data.sheet.slots > 0 && q.data.sheet.first_slot
            ? `${hhmm(q.data.sheet.first_slot)}–${hhmm(q.data.sheet.last_slot ?? '')}`
            : undefined} />
        <Tile label="Member places given up" value={e.member_places_given_up}
          note="what the event costs the membership" />
      </div>

      {/* Entries sit above the field, because on an event that takes them
          the field is a CONSEQUENCE of the entries — a name typed
          straight onto the field of a member-guest is a name nobody has
          billed. */}
      {e.members_may_enter && (
        <Entries eventId={eventId} event={e} canManage={canManage} onChanged={reload} />
      )}

      <Competitions eventId={eventId} canManage={canManage} />

      {note && <div className="notice">{note}</div>}
      <ErrorBanner
        error={claim.error ?? release.error ?? draw.error ?? addPlayer.error
          ?? setStatus.error ?? withdraw.error ?? removePlayer.error} />

      {canManage && live && (
        <div className="actions" style={{ marginBottom: 14 }}>
          {e.slots_held === 0 ? (
            <button className="gold small" disabled={claim.busy} onClick={() => claim.run()}>
              {claim.busy ? 'Taking…' : 'Take the sheet'}
            </button>
          ) : (
            <button className="secondary small" disabled={release.busy}
              onClick={() => release.run()}>
              {release.busy ? 'Handing back…' : 'Hand the sheet back'}
            </button>
          )}
          <DeleteButton label="Cancel the event" confirmLabel="Yes, cancel it"
            busy={setStatus.busy} onDelete={() => setStatus.run('cancelled')} />
          <button className="secondary small" disabled={setStatus.busy}
            onClick={() => setStatus.run('played')}>Mark played</button>
        </div>
      )}

      {e.slots_held === 0 && live && (
        <p className="muted small">
          The members can still book this morning. Nothing is off the sheet until somebody
          takes it.
        </p>
      )}

      {e.start_style === 'shotgun' && canEnter && live && (
        <div className="actions" style={{ marginBottom: 14 }}>
          <label className="field" style={{ marginBottom: 0 }}>
            <span>Players per group</span>
            <input type="number" min={1} max={5} value={perGroup}
              onChange={(ev) => setPerGroup(ev.target.value)} style={{ width: 90 }} />
          </label>
          <button className="secondary small" disabled={draw.busy} onClick={() => draw.run()}>
            {draw.busy ? 'Drawing…' : e.drawn > 0 ? 'Draw again' : 'Draw the shotgun'}
          </button>
          {/* The draw is the arithmetic, not the judgement. Every club
              pairs by handicap and splits couples differently, and every
              club does it by hand on the Thursday. */}
          <span className="muted small">
            Groups in entry order onto holes 1 upward. Move anyone by hand afterwards.
          </span>
        </div>
      )}

      <div className="row" style={{ marginBottom: 8 }}>
        <strong>The field</strong>
        <div className="spacer" />
        {canEnter && live && !adding && (
          <button className="gold small" onClick={() => setAdding(true)}>Enter somebody</button>
        )}
      </div>

      {adding && (
        <form onSubmit={(ev) => { ev.preventDefault(); addPlayer.run(); }}>
          <div className="grid-2">
            <label className="field">
              <span>Name</span>
              <input required value={entry.display_name} placeholder="R. Boyd"
                onChange={(ev) => setEntry({ ...entry, display_name: ev.target.value })} />
              {/* An outing is a hundred and twenty people who came once.
                  Creating a member record for each is how a roster fills
                  with names nobody recognizes. */}
              <div className="hint">
                A typed name is enough for an outside player — no member record needed.
              </div>
            </label>
            <label className="field">
              <span>Playing as</span>
              <select value={entry.player_kind}
                onChange={(ev) => setEntry({ ...entry, player_kind: ev.target.value })}>
                <option value="outside">Outside player</option>
                <option value="member_guest">Member's guest</option>
                <option value="member">Member</option>
              </select>
            </label>
          </div>
          <div className="actions">
            <button className="gold" disabled={addPlayer.busy}>
              {addPlayer.busy ? 'Entering…' : 'Enter'}
            </button>
            <button type="button" className="secondary" onClick={() => setAdding(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {q.data.field.length === 0 ? (
        <Empty>Nobody entered yet.</Empty>
      ) : (
        <table className="data">
          <thead>
            <tr>
              <th className="num">Group</th>
              <th className="num">Hole</th>
              <th>Name</th>
              <th>Playing as</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {q.data.field.map((p) => (
              <tr key={p.id} style={p.status === 'withdrawn' ? { opacity: 0.5 } : undefined}>
                <td className="num">{p.group_no ?? '—'}</td>
                <td className="num">{p.starting_hole ?? '—'}</td>
                <td>
                  {p.name}
                  {p.membership_number && <div className="hint">{p.membership_number}</div>}
                </td>
                <td className="muted small">{titleCase(p.player_kind.replace(/_/g, ' '))}</td>
                <td className="muted small">{titleCase(p.status)}</td>
                <td>
                  {canEnter && live && p.status !== 'withdrawn' && (
                    <Actions>
                      <button className="secondary small" disabled={withdraw.busy}
                        onClick={() => withdraw.run(p.id)}>Withdraw</button>
                      {/* A straight delete is right here and almost
                          nowhere else in this module: an entry explains
                          nothing that has happened yet. Once the event is
                          played the server refuses it. */}
                      <DeleteButton confirmLabel="Remove from the field"
                        busy={removePlayer.busy} onDelete={() => removePlayer.run(p.id)} />
                    </Actions>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

/**
 * Minor units back into something typeable.
 *
 * Not `money()`, which formats for reading — "$340.00" pasted into a
 * number field and sent back through parseMoney is a round trip that
 * works until somebody's locale puts the symbol on the other side.
 */
function toInput(minor: string | number | null | undefined, exponent: number): string {
  if (minor === null || minor === undefined || minor === '') return '';
  const n = Number(minor);
  if (!Number.isFinite(n) || n === 0) return '';
  return (n / 10 ** exponent).toFixed(exponent);
}

/**
 * The seasons the club runs.
 *
 * One layer above a competition, and the reason most members entered the
 * competition. This replaces the spreadsheet on the assistant
 * professional's laptop — which is on one machine, is updated on a
 * Monday if anybody remembers, and does not move when a card is
 * corrected on Tuesday.
 *
 * The list is deliberately thin. Everything worth looking at is inside
 * one season, so the list's whole job is to get somebody into the right
 * one and to say, before they open it, whether anything has been left
 * out of it.
 */
interface LiveCard {
  score_id: string;
  person_id: string;
  player_name: string;
  course_name: string;
  started_hole: number;
  holes_played: number;
  current_hole: number | null;
  gross: number | null;
  to_par: number | null;
  last_hole_at: string | null;
  minutes_since: number | null;
  marker_name: string | null;
}

/**
 * Where everybody has got to.
 *
 * A by-product of members keeping their own cards rather than a thing
 * built for its own sake: a card that is being kept knows how many holes
 * are on it and when the last one went on, and with the starting hole
 * that is where a group is standing.
 *
 * The minutes are reported and not judged. What counts as slow is a
 * club's decision and depends on the day, the field and the weather, and
 * a screen that decides it here would be wrong at four o'clock on a
 * Saturday in July. Sorting by the longest gap puts whoever most needs
 * looking at on top without calling them anything.
 */
function OnTheCourse() {
  const [tick, setTick] = useState(0);
  const q = useApi<{ cards: LiveCard[] }>('/v1/golf/on-the-course', [tick]);

  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 120000);
    return () => clearInterval(t);
  }, []);

  const cards = q.data?.cards ?? [];
  // Nothing to say before anybody has started, and an empty card on the
  // screen every winter morning is a card people learn to ignore.
  if (q.loading || cards.length === 0) return null;

  const waiting = [...cards].sort(
    (a, b) => (b.minutes_since ?? 0) - (a.minutes_since ?? 0));

  return (
    <Card
      title="Out on the Course"
      actions={<span className="muted small">{cards.length} keeping a card</span>}
    >
      <ErrorBanner error={q.error} />
      {/* Said because "Who is out" sits directly above and means
          something else: that list is groups the shop checked in, this
          one is people who are entering a score. A member can be on
          either without being on the other. */}
      <p className="muted small">
        Members entering their own scores — which is not the same list as the groups
        checked in above. The minutes are how long since a hole went on the card.
      </p>
      <table className="data">
        <thead>
          <tr>
            <th>Player</th><th className="num">Hole</th>
            <th className="num">Thru</th><th className="num">Score</th>
            <th className="num">Last entry</th>
          </tr>
        </thead>
        <tbody>
          {waiting.map((c) => (
            <tr key={c.score_id}>
              <td>
                {c.player_name}
                {c.marker_name && c.marker_name !== c.player_name && (
                  <div className="hint">kept by {c.marker_name}</div>
                )}
              </td>
              <td className="num mono">
                {c.current_hole ?? <span className="muted">in</span>}
                {c.started_hole !== 1 && (
                  <div className="hint">from {c.started_hole}</div>
                )}
              </td>
              <td className="num">{c.holes_played}</td>
              <td className="num">
                {c.to_par === null ? '—'
                  : c.to_par === 0 ? 'E' : c.to_par > 0 ? `+${c.to_par}` : c.to_par}
              </td>
              <td className="num">
                {/* Minutes, not a verdict. */}
                {c.minutes_since === null ? '—' : `${c.minutes_since} min`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function Series({ canManage }: { canManage: boolean }) {
  const [reload, setReload] = useState(0);
  const [includePast, setIncludePast] = useState(false);
  const q = useApi<{ series: SeriesRow[] }>(
    `/v1/golf/series?include_past=${includePast}`, [reload, includePast]);
  const [open, setOpen] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [ladderFor, setLadderFor] = useState<string | null>(null);
  const [f, setF] = useState({
    name: '', starts_on: yearStart(), ends_on: yearEnd(),
    scoring: 'placing', counts_best: '', minimum_played: '', points_beyond: '0',
  });

  const bump = () => setReload((n) => n + 1);

  const create = useMutation(async () => {
    const made = await api<{ series: SeriesRow }>('/v1/golf/series', {
      method: 'POST',
      body: {
        name: f.name.trim(),
        starts_on: f.starts_on,
        ends_on: f.ends_on,
        scoring: f.scoring,
        counts_best: f.counts_best ? Number(f.counts_best) : null,
        minimum_played: f.minimum_played ? Number(f.minimum_played) : null,
        points_beyond: Number(f.points_beyond) || 0,
      },
    });
    setAdding(false);
    setOpen(made.series.series_id);
    bump();
  });

  const rows = q.data?.series ?? [];

  return (
    <>
      <Card
        title="Seasons and Orders of Merit"
        actions={
          <div className="actions">
            <button className="secondary small"
              onClick={() => setIncludePast(!includePast)}>
              {includePast ? 'Only current' : 'Show finished ones'}
            </button>
            {canManage && !adding && (
              <button className="gold small" onClick={() => setAdding(true)}>
                Start a season
              </button>
            )}
          </div>
        }
      >
        <ErrorBanner error={q.error ?? create.error} />

        <p className="muted small">
          A season is competitions added together. Nothing counts towards one until
          somebody links it, and each season says on this list how many competitions
          were played inside its dates and are not counting.
        </p>

        {adding && (
          <form className="form-rows" style={{ maxWidth: 640 }}
            onSubmit={(e) => { e.preventDefault(); create.run(); }}>
            <div className="grid-2">
              <label className="field">
                <span>Name</span>
                <input required value={f.name} placeholder="Order of Merit 2026"
                  onChange={(e) => setF({ ...f, name: e.target.value })} />
              </label>
              <label className="field">
                <span>Scoring</span>
                <select value={f.scoring}
                  onChange={(e) => setF({ ...f, scoring: e.target.value })}>
                  <option value="placing">Points for where they finished</option>
                  <option value="stableford">Their own Stableford points</option>
                </select>
              </label>
              <label className="field">
                <span>Runs from</span>
                <input type="date" required value={f.starts_on}
                  onChange={(e) => setF({ ...f, starts_on: e.target.value })} />
              </label>
              <label className="field">
                <span>Until</span>
                <input type="date" required value={f.ends_on}
                  onChange={(e) => setF({ ...f, ends_on: e.target.value })} />
              </label>
              <label className="field">
                <span>Count each member&apos;s best</span>
                <input type="number" min={1} max={100} value={f.counts_best}
                  placeholder="all of them"
                  onChange={(e) => setF({ ...f, counts_best: e.target.value })} />
                <div className="hint">
                  Leave it blank to count everything — the harsher choice, and the one
                  that punishes the member who turns up.
                </div>
              </label>
              <label className="field">
                <span>Rounds needed to appear</span>
                <input type="number" min={1} max={100} value={f.minimum_played}
                  placeholder="none"
                  onChange={(e) => setF({ ...f, minimum_played: e.target.value })} />
                <div className="hint">
                  Below this a member is named separately rather than ranked at the
                  bottom.
                </div>
              </label>
              <label className="field">
                <span>Points outside the ladder</span>
                <input type="number" min={0} step="0.5" value={f.points_beyond}
                  onChange={(e) => setF({ ...f, points_beyond: e.target.value })} />
                <div className="hint">
                  Usually nothing. Sometimes one, because a club that gives a point for
                  turning up gets more people turning up.
                </div>
              </label>
            </div>
            <div className="actions">
              <button className="gold" type="submit" disabled={create.busy}>
                Start it
              </button>
              <button className="secondary" type="button"
                onClick={() => setAdding(false)}>Cancel</button>
            </div>
          </form>
        )}

        {q.loading && rows.length === 0 ? <Loading /> : rows.length === 0 ? (
          <Empty>
            No season running. An order of merit is what most members are actually
            playing for — the medal is one afternoon, the table is the year.
          </Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Season</th><th>Runs</th>
                <th className="num">Counting</th><th className="num">Players</th>
                <th>State</th><th />
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.series_id}>
                  <td>
                    {s.name}
                    <div className="hint">
                      {s.counts_best ? `best ${s.counts_best}` : 'every result counts'}
                      {s.minimum_played ? ` · qualify on ${s.minimum_played}` : ''}
                    </div>
                  </td>
                  <td className="small">
                    {fmtDate(s.starts_on)} – {fmtDate(s.ends_on)}
                  </td>
                  <td className="num">
                    {s.competitions}
                    {/* The nudge. A club that forgot to link last Saturday
                        finds out here rather than at the presentation. */}
                    {s.not_counting > 0 && (
                      <div className="hint">{s.not_counting} not counting</div>
                    )}
                  </td>
                  <td className="num">
                    {s.players}
                    {s.short_of_qualifying > 0 && (
                      <div className="hint">{s.short_of_qualifying} short</div>
                    )}
                  </td>
                  <td>
                    {s.status === 'final'
                      ? <Status tone="good">Declared</Status>
                      : s.status === 'abandoned'
                        ? <span className="muted small">Abandoned</span>
                        : <Status tone="neutral">Running</Status>}
                  </td>
                  <td className="actions">
                    <button className="secondary small"
                      onClick={() => setOpen(open === s.series_id ? null : s.series_id)}>
                      {open === s.series_id ? 'Close' : 'Open'}
                    </button>
                    {canManage && s.status !== 'final' && (
                      <button className="secondary small"
                        onClick={() => setLadderFor(
                          ladderFor === s.series_id ? null : s.series_id)}>
                        Points table
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {ladderFor && <Ladder seriesId={ladderFor} onSaved={bump} />}
      {open && (
        <Standings seriesId={open} canManage={canManage}
          reloadKey={reload} onChanged={bump} />
      )}
    </>
  );
}

/**
 * What each finishing place is worth.
 *
 * Edited as a whole rather than a row at a time. A points table is read
 * down as a shape — steep at the top, flat in the middle — and changing
 * it through six requests leaves five intermediate states in which the
 * season is briefly wrong.
 */
function Ladder({ seriesId, onSaved }: { seriesId: string; onSaved: () => void }) {
  const [reload, setReload] = useState(0);
  const q = useApi<{ ladder: { place: number; points: string | number }[];
    series: SeriesRow }>(`/v1/golf/series/${seriesId}`, [seriesId, reload]);
  const [text, setText] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const current = q.data?.ladder ?? [];
  const asText = current.map((l) => seriesPoints(l.points)).join(', ');

  const save = useMutation(async () => {
    const parsed = (text ?? asText)
      .split(/[,\s]+/).map((v) => v.trim()).filter(Boolean).map(Number);
    if (parsed.some((n) => !Number.isFinite(n) || n < 0)) {
      throw new Error('The points table should be numbers, first place first.');
    }
    await api(`/v1/golf/series/${seriesId}/ladder`, {
      method: 'PUT', body: { points: parsed },
    });
    setNote('Saved. Every table that reads this season has moved.');
    setText(null);
    setReload((n) => n + 1);
    onSaved();
  });

  return (
    <Card title={`Points table — ${q.data?.series.name ?? ''}`}>
      {note && <div className="notice">{note}</div>}
      <ErrorBanner error={q.error ?? save.error} />

      <p className="muted small">
        First place first. Anything past the end of this list is worth{' '}
        {seriesPoints(q.data?.series.points_beyond ?? 0)}.
      </p>

      <label className="field" style={{ maxWidth: 560 }}>
        <span>Points, best first</span>
        <input value={text ?? asText} placeholder="20, 16, 14, 12, 10"
          onChange={(e) => setText(e.target.value)} />
        <div className="hint">
          {/* The rule that looks like an error until it is explained. */}
          A place shared by two players is worth the average of the two places they
          hold between them — tied for second on this table is 14, not 16, and the
          player behind them is fourth.
        </div>
      </label>

      <div className="actions">
        <button className="gold small" disabled={save.busy}
          onClick={() => save.run()}>Save the table</button>
        {text !== null && (
          <button className="secondary small" onClick={() => setText(null)}>Undo</button>
        )}
      </div>
    </Card>
  );
}

function Events({ canManage, canEnter }: { canManage: boolean; canEnter: boolean }) {
  const { currency } = useSession();
  const [includePast, setIncludePast] = useState(false);
  const q = useApi<{ events: GolfEvent[] }>(
    `/v1/golf/events?include_past=${includePast}`, [includePast]);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const body = (v: typeof BLANK_EVENT) => ({
    name: v.name,
    kind: v.kind,
    play_date: v.play_date,
    start_style: v.start_style,
    window_from: v.window_from,
    window_to: v.window_to,
    shotgun_at: v.start_style === 'shotgun' ? v.shotgun_at : null,
    holes: Number(v.holes),
    max_players: v.max_players ? Number(v.max_players) : null,
    notes: v.notes || null,
    members_may_enter: v.members_may_enter,
    entries_open_on: v.entries_open_on || null,
    entries_close_on: v.entries_close_on || null,
    // parseMoney because the form takes dollars and the database keeps
    // minor units — the one conversion in this file, done in one place.
    entry_fee_minor: v.entry_fee ? parseMoney(v.entry_fee, currency) : null,
    entry_fee_per: v.entry_fee_per,
    guest_fee_minor: v.guest_fee ? parseMoney(v.guest_fee, currency) : null,
    revenue_category: (v.entry_fee || v.guest_fee) ? v.revenue_category : null,
    fee_includes: v.fee_includes || null,
    players_per_entry: v.players_per_entry ? Number(v.players_per_entry) : null,
  });

  const create = useMutation(async (v: typeof BLANK_EVENT) => {
    const r = await api<{ event: GolfEvent; bookings_in_the_way: number }>('/v1/golf/events', {
      method: 'POST', body: body(v),
    });
    setAdding(false);
    // Said now rather than when somebody presses "take the sheet" three
    // weeks later and is refused. This is the number that decides whether
    // the date is workable at all.
    setNote(
      r.bookings_in_the_way > 0
        ? `Planned. ${r.bookings_in_the_way} group(s) are already booked inside that ` +
          'window — they will have to be moved before the sheet can be taken.'
        : 'Planned. Nothing is booked in that window.',
    );
    q.reload();
  });

  const update = useMutation(async (id: string, v: typeof BLANK_EVENT) => {
    await api(`/v1/golf/events/${id}`, { method: 'PATCH', body: body(v) });
    setEditing(null);
    setNote('Saved.');
    q.reload();
  });

  const remove = useMutation(async (id: string) => {
    await api(`/v1/golf/events/${id}`, { method: 'DELETE' });
    setNote('Removed.');
    q.reload();
  });

  const events = q.data?.events ?? [];

  return (
    <>
      <Card
        title="Events"
        actions={
          <div className="actions">
            <button className="secondary small" onClick={() => setIncludePast(!includePast)}>
              {includePast ? 'Coming up only' : 'Show past ones'}
            </button>
            {canManage && !adding && (
              <button className="gold small" onClick={() => setAdding(true)}>Plan an event</button>
            )}
          </div>
        }
      >
        <p className="muted small">
          The days the club takes the course back. Planning an event does not block anything —
          the sheet is taken as a separate, deliberate step, and handing it back restores
          whatever each slot was rather than opening it.
        </p>

        {note && <div className="notice">{note}</div>}

        {adding && (
          <EventForm initial={BLANK_EVENT} submitLabel="Plan it" busy={create.busy}
            error={create.error} onSubmit={(v) => create.run(v)}
            onCancel={() => setAdding(false)} />
        )}

        <ErrorBanner error={q.error ?? update.error ?? remove.error} />
        {q.loading ? <Loading /> : events.length === 0 ? (
          <Empty>{includePast ? 'No events at all.' : 'Nothing coming up.'}</Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Day</th>
                <th>Event</th>
                <th>Window</th>
                <th className="num">Field</th>
                <th>Sheet</th>
                <th className="num">Places given up</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {events.map((e) => editing === e.id ? (
                <tr key={e.id}>
                  <td colSpan={7}>
                    <EventForm
                      initial={{
                        name: e.name,
                        kind: e.kind,
                        play_date: e.play_date,
                        start_style: e.start_style,
                        window_from: hhmm(e.window_from),
                        window_to: hhmm(e.window_to),
                        shotgun_at: e.shotgun_at ? hhmm(e.shotgun_at) : '08:00',
                        holes: String(e.holes),
                        max_players: e.max_players ? String(e.max_players) : '',
                        notes: e.notes ?? '',
                        members_may_enter: e.members_may_enter ?? false,
                        entries_open_on: e.entries_open_on ?? '',
                        entries_close_on: e.entries_close_on ?? '',
                        entry_fee: toInput(e.entry_fee_minor, currency.exponent),
                        entry_fee_per: e.entry_fee_per ?? 'entry',
                        guest_fee: toInput(e.guest_fee_minor, currency.exponent),
                        revenue_category: e.revenue_category ?? 'tournament_fee',
                        fee_includes: e.fee_includes ?? '',
                        players_per_entry: e.players_per_entry
                          ? String(e.players_per_entry) : '',
                      }}
                      submitLabel="Save"
                      busy={update.busy}
                      error={update.error}
                      onSubmit={(v) => update.run(e.id, v)}
                      onCancel={() => setEditing(null)}
                    />
                  </td>
                </tr>
              ) : (
                <tr key={e.id}>
                  <td>
                    {fmtDate(e.play_date)}
                    <div className="hint">{KIND_LABEL[e.kind] ?? e.kind}</div>
                  </td>
                  <td>
                    <strong>{e.name}</strong>
                    {e.notes && <div className="hint">{e.notes}</div>}
                  </td>
                  <td className="mono small">
                    {hhmm(e.window_from)}–{hhmm(e.window_to)}
                    {e.start_style === 'shotgun' && (
                      <div className="hint">shotgun {hhmm(e.shotgun_at ?? '')}</div>
                    )}
                  </td>
                  <td className="num">
                    {e.field_size}{e.max_players ? ` / ${e.max_players}` : ''}
                    {e.drawn > 0 && <div className="hint">drawn</div>}
                  </td>
                  <td><SheetState e={e} /></td>
                  <td className="num">{e.member_places_given_up || '—'}</td>
                  <td>
                    <Actions>
                      <button className="secondary small"
                        onClick={() => setOpen(open === e.id ? null : e.id)}>
                        {open === e.id ? 'Hide' : 'Open'}
                      </button>
                      {canManage && (
                        <>
                          <button className="secondary small" onClick={() => setEditing(e.id)}>
                            Edit
                          </button>
                          {/* Offered only where the server would allow it.
                              An event with a field or a claim on the sheet
                              is cancelled, not deleted. */}
                          {e.field_size === 0 && e.slots_held === 0 && (
                            <DeleteButton confirmLabel="Delete the event" busy={remove.busy}
                              onDelete={() => remove.run(e.id)} />
                          )}
                        </>
                      )}
                    </Actions>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {open && (
        <EventDetail
          eventId={open}
          canManage={canManage}
          canEnter={canEnter}
          onChanged={() => q.reload()}
          onClose={() => setOpen(null)}
        />
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* The numbers                                                         */
/*                                                                     */
/* Four views from migration 0069 that have never had a way in, and a  */
/* forecast from 0080 that refuses to guess.                           */
/*                                                                     */
/* Two things are said out loud on this screen rather than assumed:    */
/* where the distance figure comes from, and how much history is       */
/* behind each forecast. A club will quote both at a board meeting.    */
/* ------------------------------------------------------------------ */

interface RoundsPayload {
  course_id: string;
  period: Record<string, number | string>;
  travel: {
    walked_rounds: number;
    rode_rounds: number;
    unverified_rounds: number;
    walked_card_yards: number;
    rode_card_yards: number;
    walked_distance: { value: number; unit: string };
    rode_distance: { value: number; unit: string };
    estimate_note: string;
  };
  demand: {
    day_of_week: number; hour_of_day: number; rounds: number;
    days_observed: number; rounds_per_day_observed: string;
    rode: number; walked: number;
  }[];
  quality: {
    month_starting: string; player_slots: number; played: number;
    no_show: number; canceled: number; still_open: number;
    no_show_pct: string; travel_unverified_pct: string;
  }[];
  forecast: {
    play_date: string; expected: string | null; baseline: string | null;
    adjustment: string; weather_bucket: string; same_weekday_days: number;
    bucket_days: number; confidence: string; basis: string;
  }[];
}

const DOW = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const CONFIDENCE_TONE: Record<string, 'good' | 'warning' | 'neutral' | 'critical'> = {
  good: 'good', fair: 'good', weak: 'warning', none: 'neutral',
};

function Numbers() {
  const q = useApi<RoundsPayload>('/v1/golf/rounds');

  if (q.loading) return <Card><Loading /></Card>;
  if (!q.data) return <Card><ErrorBanner error={q.error} /></Card>;

  const p = q.data.period;
  const t = q.data.travel;
  const n = (v: unknown) => Number(v ?? 0);

  // Day by hour. Rounds per day observed rather than raw counts: a
  // Saturday eight o'clock seen ten times and a Tuesday eight o'clock
  // seen forty times are not the same tee time, and the raw count says
  // they are.
  const byDay = new Map<number, typeof q.data.demand>();
  for (const d of q.data.demand) {
    if (!byDay.has(d.day_of_week)) byDay.set(d.day_of_week, []);
    byDay.get(d.day_of_week)!.push(d);
  }
  const busiest = [...q.data.demand]
    .sort((a, b) => Number(b.rounds_per_day_observed) - Number(a.rounds_per_day_observed))
    .slice(0, 8);

  return (
    <>
      <Card title="Rounds">
        <div className="tiles">
          <Tile label="Today" value={n(p.rounds_today)}
            note={n(p.rounds_on_course_now) > 0
              ? `${n(p.rounds_on_course_now)} on the course now` : undefined} />
          <Tile label="This week" value={n(p.rounds_week_to_date)} />
          <Tile label="This month" value={n(p.rounds_month_to_date)} />
          <Tile label="This year" value={n(p.rounds_year_to_date)} />
          <Tile label="Last 365 days" value={n(p.rounds_last_365)} />
        </div>
        <p className="muted small">
          A round is one player, not one group — the number a superintendent means by
          "we did 180 on Saturday". Today's figure counts groups already on the course;
          everything else counts rounds finished.
        </p>
      </Card>

      <Card title="Walked or Carted">
        <div className="tiles">
          <Tile label="Walked" value={t.walked_rounds}
            note={`${t.walked_distance.value.toLocaleString()} ${t.walked_distance.unit}`} />
          <Tile label="Carted" value={t.rode_rounds}
            note={`${t.rode_distance.value.toLocaleString()} ${t.rode_distance.unit}`} />
          <Tile label="Not known" value={t.unverified_rounds}
            tone={t.unverified_rounds > 0 ? 'warning' : undefined}
            note={t.unverified_rounds > 0 ? 'no cart fee and no cart out' : undefined} />
          <Tile label="Nine holes" value={n(p.nine_hole_ytd)} />
          <Tile label="Eighteen" value={n(p.eighteen_hole_ytd)} />
        </div>

        {/* The number nobody can check is the number nobody should trust.
            The multiplier and the exact card totals are both here. */}
        <div className="hint">
          {t.estimate_note} Card totals: {t.walked_card_yards.toLocaleString()} yards walked,
          {' '}{t.rode_card_yards.toLocaleString()} carted.
        </div>
      </Card>

      <Card title="What the Next Week Looks Like">
        <p className="muted small">
          Worked out from how many rounds this course usually takes on that weekday,
          adjusted for the forecast. Every row shows what it is based on — a number with
          three Saturdays behind it is not a forecast, and this says so rather than
          rounding to something plausible.
        </p>
        <table className="data">
          <thead>
            <tr>
              <th>Day</th>
              <th className="num">Expected</th>
              <th>Conditions</th>
              <th>Based on</th>
            </tr>
          </thead>
          <tbody>
            {q.data.forecast.map((f) => (
              <tr key={f.play_date}>
                <td>
                  {fmtDate(f.play_date)}
                  <div className="hint">{DOW[new Date(f.play_date + 'T12:00:00Z').getUTCDay() || 7]}</div>
                </td>
                <td className="num">
                  {f.expected === null
                    ? <span className="muted">—</span>
                    : <strong style={{ fontSize: 18 }}>{f.expected}</strong>}
                </td>
                <td>
                  {f.weather_bucket === 'unknown'
                    ? <span className="muted small">no forecast held</span>
                    : titleCase(f.weather_bucket)}
                  {Number(f.adjustment) !== 1 && (
                    <div className="hint">
                      {Number(f.adjustment) > 1 ? '+' : ''}
                      {Math.round((Number(f.adjustment) - 1) * 100)}%
                    </div>
                  )}
                </td>
                <td className="muted small">
                  <Status tone={CONFIDENCE_TONE[f.confidence] ?? 'neutral'}>
                    {titleCase(f.confidence)}
                  </Status>
                  <div className="hint">{f.basis}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card title="Popular Times">
        {q.data.demand.length === 0 ? (
          <Empty>
            Nothing played yet. This fills in as rounds are checked in — it counts what
            was played, not what was booked.
          </Empty>
        ) : (
          <>
            <p className="muted small">
              Rounds per day observed, so a tee time seen ten times and one seen forty
              times can be compared. The busiest eight:
            </p>
            {/* Hand-rolled rather than the shared bar list, which formats
                every value as money — a Saturday eight o'clock is 4.2
                rounds a day, not $4.20. */}
            <div className="bars">
              {busiest.map((d) => {
                const top = Number(busiest[0]!.rounds_per_day_observed) || 1;
                const v = Number(d.rounds_per_day_observed);
                return (
                  <div className="bar-row" key={`${d.day_of_week}-${d.hour_of_day}`}>
                    <div className="bar-row__label">
                      {DOW[d.day_of_week]} {String(d.hour_of_day).padStart(2, '0')}:00
                    </div>
                    <div className="bar-row__track">
                      <div className="bar-row__fill"
                        style={{ width: `${Math.max((v / top) * 100, 2)}%`,
                          background: 'var(--series-1)' }} />
                    </div>
                    <div className="bar-row__value">{v}</div>
                  </div>
                );
              })}
            </div>
            <details>
              <summary className="muted small" style={{ cursor: 'pointer', margin: '10px 0' }}>
                Every hour, by day
              </summary>
              <table className="data">
                <thead>
                  <tr>
                    <th>Day</th><th className="num">Hour</th>
                    <th className="num">Rounds</th><th className="num">Days seen</th>
                    <th className="num">Per day</th>
                    <th className="num">Walked</th><th className="num">Carted</th>
                  </tr>
                </thead>
                <tbody>
                  {[...byDay.entries()].sort((a, b) => a[0] - b[0]).flatMap(([dow, rows]) =>
                    rows.map((d, i) => (
                      <tr key={`${dow}-${d.hour_of_day}`}>
                        <td>{i === 0 ? DOW[dow] : ''}</td>
                        <td className="num mono">
                          {String(d.hour_of_day).padStart(2, '0')}:00
                        </td>
                        <td className="num">{d.rounds}</td>
                        <td className="num">{d.days_observed}</td>
                        <td className="num"><strong>{d.rounds_per_day_observed}</strong></td>
                        <td className="num">{d.walked}</td>
                        <td className="num">{d.rode}</td>
                      </tr>
                    )))}
                </tbody>
              </table>
            </details>
          </>
        )}
      </Card>

      {q.data.quality.length > 0 && (
        <Card title="How Good the Numbers Are">
          <p className="muted small">
            Every figure above rests on the sheet being kept. A month with a high
            unverified share is a month where nobody recorded whether groups walked —
            the rounds are real, the walking is a guess.
          </p>
          <table className="data">
            <thead>
              <tr>
                <th>Month</th><th className="num">Played</th><th className="num">No-show</th>
                <th className="num">Cancelled</th><th className="num">No-show rate</th>
                <th className="num">Travel unknown</th>
              </tr>
            </thead>
            <tbody>
              {q.data.quality.map((m) => (
                <tr key={m.month_starting}>
                  <td>{fmtDate(m.month_starting)}</td>
                  <td className="num">{m.played}</td>
                  <td className="num">{m.no_show}</td>
                  <td className="num">{m.canceled}</td>
                  <td className="num">{m.no_show_pct ?? '—'}%</td>
                  <td className="num">
                    {Number(m.travel_unverified_pct) > 25
                      ? <Status tone="warning">{m.travel_unverified_pct}%</Status>
                      : `${m.travel_unverified_pct ?? 0}%`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* The practice center                                                 */
/* ------------------------------------------------------------------ */

interface Facility {
  id: string;
  code: string;
  name: string;
  kind: string;
  is_bookable: boolean;
  capacity: number;
  min_minutes: number;
  max_minutes: number;
  fee_kind: string;
  status: string;
  opens_at: string | null;
  closes_at: string | null;
  notes: string | null;
  bookings_that_day: number;
}

interface PracticeBooking {
  booking_id: string;
  facility_id: string;
  facility_name: string;
  local_start: string;
  local_end: string;
  duration_minutes: number;
  party_size: number;
  status: string;
  amount_minor: number | null;
  ar_transaction_id: string | null;
  booked_by_name: string | null;
  account_name: string | null;
  notes: string | null;
}

const FACILITY_KIND: Record<string, string> = {
  simulator: 'Simulator',
  range: 'Range',
  short_game: 'Short game',
  putting_green: 'Putting green',
  studio: 'Studio',
  other: 'Other',
};

function Practice({ canBook, canManage }: { canBook: boolean; canManage: boolean }) {
  const [date, setDate] = useState(today());
  const q = useApi<{ date: string; facilities: Facility[]; bookings: PracticeBooking[] }>(
    `/v1/golf/practice?date=${date}`, [date]);
  const { currency } = useSession();
  const [booking, setBooking] = useState<Facility | null>(null);
  const [form, setForm] = useState({ start: '10:00', minutes: '60', party: '1' });
  const [note, setNote] = useState<string | null>(null);

  const create = useMutation(async (f: Facility) => {
    // The instant, not the wall clock. The server checks it against the
    // bay's own opening hours in the club's timezone, so sending a naked
    // local time would be a different hour for a member in Arizona.
    const starts = new Date(`${date}T${form.start}:00`).toISOString();
    await api('/v1/golf/practice/bookings', {
      method: 'POST',
      body: {
        facility_id: f.id,
        starts_at: starts,
        duration_minutes: Number(form.minutes),
        party_size: Number(form.party),
      },
    });
    setBooking(null);
    setNote(`Booked ${f.name} at ${form.start}.`);
    q.reload();
  });

  const checkIn = useMutation(async (id: string) => {
    const r = await api<{ charged: boolean; note: string }>(
      `/v1/golf/practice/bookings/${id}/check-in`, { method: 'POST' });
    setNote(r.note);
    q.reload();
  });

  const remove = useMutation(async (id: string) => {
    const r = await api<{ cancelled?: boolean; note?: string }>(
      `/v1/golf/practice/bookings/${id}`, { method: 'DELETE' });
    setNote(r?.note ?? 'Removed.');
    q.reload();
  });

  const facilities = q.data?.facilities ?? [];
  const bookings = q.data?.bookings ?? [];

  return (
    <>
      <Card
        title="The Practice Center"
        actions={
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        }
      >
        <p className="muted small">
          Golf that is not the course. A putting green is walked onto; a simulator is
          booked by the hour and charged from the same rate card as a green fee, at the
          member's own class.
        </p>

        {note && <div className="notice">{note}</div>}
        <ErrorBanner error={q.error ?? create.error ?? checkIn.error ?? remove.error} />

        {q.loading ? <Loading /> : facilities.length === 0 ? (
          <Empty>Nothing set up yet.</Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Facility</th><th>Kind</th><th>Hours</th>
                <th className="num">Holds</th><th>Booking</th>
                <th className="num">On {fmtDate(date)}</th><th />
              </tr>
            </thead>
            <tbody>
              {facilities.map((f) => (
                <tr key={f.id} style={f.status !== 'available' ? { opacity: 0.6 } : undefined}>
                  <td>
                    <strong>{f.name}</strong>
                    {f.notes && <div className="hint">{f.notes}</div>}
                  </td>
                  <td className="muted small">{FACILITY_KIND[f.kind] ?? f.kind}</td>
                  <td className="mono small">
                    {f.opens_at ? `${hhmm(f.opens_at)}–${hhmm(f.closes_at ?? '')}` : 'any time'}
                  </td>
                  <td className="num">{f.capacity}</td>
                  <td>
                    {!f.is_bookable
                      ? <span className="muted small">walked onto</span>
                      : f.status !== 'available'
                        ? <Status tone="warning">{titleCase(f.status)}</Status>
                        : <span className="muted small">
                            {f.min_minutes}–{f.max_minutes} min
                          </span>}
                  </td>
                  <td className="num">{f.bookings_that_day || '—'}</td>
                  <td>
                    {canBook && f.is_bookable && f.status === 'available' && (
                      <Actions>
                        <button className="secondary small"
                          onClick={() => {
                            setBooking(f);
                            setForm({
                              start: f.opens_at ? hhmm(f.opens_at) : '10:00',
                              minutes: String(f.min_minutes),
                              party: '1',
                            });
                          }}>
                          Book
                        </button>
                      </Actions>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {booking && (
        <Card title={`Book ${booking.name}`}>
          <form onSubmit={(e) => { e.preventDefault(); create.run(booking); }}>
            <ErrorBanner error={create.error} />
            <div className="grid-2">
              <label className="field">
                <span>Start</span>
                <input required type="time" value={form.start}
                  onChange={(e) => setForm({ ...form, start: e.target.value })} />
              </label>
              <label className="field">
                <span>Minutes</span>
                <input required type="number" step={15}
                  min={booking.min_minutes} max={booking.max_minutes}
                  value={form.minutes}
                  onChange={(e) => setForm({ ...form, minutes: e.target.value })} />
                <div className="hint">
                  {booking.name} is booked in blocks of {booking.min_minutes} to{' '}
                  {booking.max_minutes} minutes.
                </div>
              </label>
              <label className="field">
                <span>Players</span>
                <input required type="number" min={1} max={booking.capacity}
                  value={form.party}
                  onChange={(e) => setForm({ ...form, party: e.target.value })} />
              </label>
            </div>
            <div className="actions">
              <button className="gold" disabled={create.busy}>
                {create.busy ? 'Booking…' : 'Book it'}
              </button>
              <button type="button" className="secondary" onClick={() => setBooking(null)}>
                Cancel
              </button>
            </div>
          </form>
        </Card>
      )}

      <Card title={`Booked on ${fmtDate(date)}`}>
        {bookings.length === 0 ? (
          <Empty>Nothing booked.</Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Time</th><th>Facility</th><th>Who</th>
                <th className="num">Players</th><th>Status</th>
                <th className="num">Charged</th><th />
              </tr>
            </thead>
            <tbody>
              {bookings.map((b) => (
                <tr key={b.booking_id}
                  style={b.status === 'canceled' ? { opacity: 0.5 } : undefined}>
                  <td className="mono">
                    {hhmm(b.local_start)}–{hhmm(b.local_end)}
                    <div className="hint">{b.duration_minutes} min</div>
                  </td>
                  <td>{b.facility_name}</td>
                  <td>
                    {b.account_name ?? b.booked_by_name ?? '—'}
                    {b.notes && <div className="hint">{b.notes}</div>}
                  </td>
                  <td className="num">{b.party_size}</td>
                  <td className="muted small">{titleCase(b.status.replace(/_/g, ' '))}</td>
                  <td className="num">
                    {b.amount_minor
                      ? <Money minor={b.amount_minor} currency={currency} />
                      : <span className="muted">—</span>}
                  </td>
                  <td>
                    {canManage && b.status === 'booked' && (
                      <Actions>
                        <button className="secondary small" disabled={checkIn.busy}
                          onClick={() => checkIn.run(b.booking_id)}>Check in</button>
                        <DeleteButton confirmLabel="Take it off" busy={remove.busy}
                          onDelete={() => remove.run(b.booking_id)} />
                      </Actions>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* The professionals                                                   */
/* ------------------------------------------------------------------ */

interface Professional {
  id: string;
  person_id: string;
  name: string;
  title: string;
  rate_class: string;
  is_active: boolean;
  sort_order: number;
  lessons_that_day: number;
  minutes_that_day: number;
}

interface DiaryPayload {
  professional_id: string;
  date: string;
  opens: string;
  closes: string;
  lessons: {
    lesson_id: string; local_start: string; local_end: string;
    duration_minutes: number; kind: string; status: string;
    student_name: string | null; amount_minor: number | null;
    ar_transaction_id: string | null; notes: string | null;
  }[];
  gaps: { from: string; to: string; minutes: number }[];
  booked_minutes: number;
}

const LESSON_KINDS = ['private', 'playing', 'clinic', 'fitting', 'evaluation'];

function Professionals({ canManage, canBook }: { canManage: boolean; canBook: boolean }) {
  const [date, setDate] = useState(today());
  const q = useApi<{ professionals: Professional[] }>(
    `/v1/golf/professionals?on=${date}&include_inactive=true`, [date]);
  const [open, setOpen] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  // The person, as chosen rather than as typed. This was a required
  // text box asking for a uuid; a professional could not be added at all.
  const [pro, setPro] = useState<PersonHit | null>(null);
  const [newPro, setNewPro] = useState({ title: 'Golf Professional',
    rate_class: 'professional' });

  const create = useMutation(async () => {
    await api('/v1/golf/professionals', {
      method: 'POST',
      body: { ...newPro, person_id: pro?.person_id ?? null },
    });
    setAdding(false);
    setPro(null);
    setNewPro({ title: 'Golf Professional', rate_class: 'professional' });
    q.reload();
  });

  const toggle = useMutation(async (id: string, active: boolean) => {
    await api(`/v1/golf/professionals/${id}`, {
      method: 'PATCH', body: { is_active: active },
    });
    q.reload();
  });

  const pros = q.data?.professionals ?? [];

  return (
    <>
      <Card
        title="The Professionals"
        actions={
          <div className="actions">
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            {canManage && !adding && (
              <button className="gold small" onClick={() => setAdding(true)}>
                Add a professional
              </button>
            )}
          </div>
        }
      >
        {note && <div className="notice">{note}</div>}
        <ErrorBanner error={q.error ?? create.error ?? toggle.error} />

        {adding && (
          <form onSubmit={(e) => { e.preventDefault(); create.run(); }}>
            <div className="grid-2">
              <PersonLookup
                label="Person"
                hint="They must already be on the roster — a professional is a person the club knows, not a name typed twice."
                value={pro}
                onChange={setPro}
                // A professional is staff, and staff are frequently not on
                // a live membership.
                includeInactive
              />
              <label className="field">
                <span>Title</span>
                <input required value={newPro.title}
                  onChange={(e) => setNewPro({ ...newPro, title: e.target.value })} />
              </label>
              <label className="field">
                <span>Rate class</span>
                <input required value={newPro.rate_class}
                  onChange={(e) => setNewPro({ ...newPro, rate_class: e.target.value })} />
                {/* This is how the lesson is priced, so it is worth
                    explaining rather than leaving as a mystery field. */}
                <div className="hint">
                  Which line of the rate card prices their hour. An assistant on a
                  different class is charged differently without changing anything else.
                </div>
              </label>
            </div>
            <div className="actions">
              <button className="gold" disabled={create.busy || !pro}>
                {create.busy ? 'Adding…' : 'Add'}
              </button>
              <button type="button" className="secondary" onClick={() => setAdding(false)}>
                Cancel
              </button>
            </div>
          </form>
        )}

        {q.loading ? <Loading /> : pros.length === 0 ? (
          <Empty>
            Nobody yet. A lesson needs somebody to teach it, so the lesson book stays
            empty until there is at least one.
          </Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Name</th><th>Title</th><th>Rate class</th>
                <th className="num">On {fmtDate(date)}</th><th />
              </tr>
            </thead>
            <tbody>
              {pros.map((p) => (
                <tr key={p.id} style={p.is_active ? undefined : { opacity: 0.55 }}>
                  <td>
                    <strong>{p.name}</strong>
                    {!p.is_active && <div className="hint">no longer teaching</div>}
                  </td>
                  <td className="muted small">{p.title}</td>
                  <td className="mono small">{p.rate_class}</td>
                  <td className="num">
                    {p.lessons_that_day || '—'}
                    {p.minutes_that_day > 0 && (
                      <div className="hint">{p.minutes_that_day} min</div>
                    )}
                  </td>
                  <td>
                    <Actions>
                      <button className="secondary small"
                        onClick={() => setOpen(open === p.id ? null : p.id)}>
                        {open === p.id ? 'Hide diary' : 'Diary'}
                      </button>
                      {canManage && (
                        <button className="secondary small" disabled={toggle.busy}
                          onClick={() => toggle.run(p.id, !p.is_active)}>
                          {p.is_active ? 'Retire' : 'Bring back'}
                        </button>
                      )}
                    </Actions>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {open && (
        <Diary professionalId={open} date={date} canBook={canBook}
          onChanged={() => { q.reload(); setNote('Saved.'); }}
          onClose={() => setOpen(null)} />
      )}
    </>
  );
}

function Diary({
  professionalId, date, canBook, onChanged, onClose,
}: {
  professionalId: string;
  date: string;
  canBook: boolean;
  onChanged: () => void;
  onClose: () => void;
}) {
  const q = useApi<DiaryPayload>(
    `/v1/golf/professionals/${professionalId}/diary?date=${date}`, [professionalId, date]);
  const { currency } = useSession();
  const [booking, setBooking] = useState<{ from: string } | null>(null);
  const [f, setF] = useState({ student: '', minutes: '60', kind: 'private' });

  const book = useMutation(async (from: string) => {
    await api('/v1/golf/lessons', {
      method: 'POST',
      body: {
        professional_id: professionalId,
        student_name: f.student,
        starts_at: new Date(`${date}T${from}:00`).toISOString(),
        duration_minutes: Number(f.minutes),
        kind: f.kind,
      },
    });
    setBooking(null);
    setF({ student: '', minutes: '60', kind: 'private' });
    q.reload();
    onChanged();
  });

  if (q.loading) return <Card><Loading /></Card>;
  if (!q.data) return <Card><ErrorBanner error={q.error} /></Card>;

  return (
    <Card
      title={`Diary — ${fmtDate(date)}`}
      actions={<button className="secondary small" onClick={onClose}>Close</button>}
    >
      <div className="tiles">
        <Tile label="Booked" value={`${Math.floor(q.data.booked_minutes / 60)}h ${
          q.data.booked_minutes % 60}m`} />
        <Tile label="Lessons" value={q.data.lessons.length} />
        <Tile label="Free" value={q.data.gaps.length}
          note={q.data.gaps.length === 1 ? 'gap' : 'gaps'} />
        <Tile label="Day" value={`${q.data.opens}–${q.data.closes}`}
          note="from the season in force" />
      </div>

      <ErrorBanner error={book.error} />

      {q.data.lessons.length === 0 ? (
        <Empty>Nothing booked.</Empty>
      ) : (
        <table className="data">
          <thead>
            <tr>
              <th>Time</th><th>Student</th><th>Kind</th>
              <th>Status</th><th className="num">Charged</th>
            </tr>
          </thead>
          <tbody>
            {q.data.lessons.map((l) => (
              <tr key={l.lesson_id}>
                <td className="mono">
                  {hhmm(l.local_start)}–{hhmm(l.local_end)}
                  <div className="hint">{l.duration_minutes} min</div>
                </td>
                <td>
                  {l.student_name ?? '—'}
                  {l.notes && <div className="hint">{l.notes}</div>}
                </td>
                <td className="muted small">{titleCase(l.kind)}</td>
                <td className="muted small">{titleCase(l.status.replace(/_/g, ' '))}</td>
                <td className="num">
                  {l.amount_minor
                    ? <Money minor={l.amount_minor} currency={currency} />
                    : <span className="muted">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* The gaps, which is what the shop is actually reading the diary
          for. "Can she take somebody at two?" answered without arithmetic. */}
      <div className="row" style={{ marginTop: 14, marginBottom: 6 }}>
        <strong>Free</strong>
      </div>
      {q.data.gaps.length === 0 ? (
        <p className="muted small">Nothing free today.</p>
      ) : (
        <table className="data">
          <thead>
            <tr><th>From</th><th>To</th><th className="num">Minutes</th><th /></tr>
          </thead>
          <tbody>
            {q.data.gaps.map((g) => (
              <tr key={g.from}>
                <td className="mono">{g.from}</td>
                <td className="mono">{g.to}</td>
                <td className="num">{g.minutes}</td>
                <td>
                  {canBook && (
                    <button className="secondary small"
                      onClick={() => setBooking({ from: g.from })}>
                      Book this
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {booking && (
        <form onSubmit={(e) => { e.preventDefault(); book.run(booking.from); }}
          style={{ marginTop: 14 }}>
          <div className="grid-2">
            <label className="field">
              <span>Student</span>
              <input required value={f.student} placeholder="Name"
                onChange={(e) => setF({ ...f, student: e.target.value })} />
            </label>
            <label className="field">
              <span>Minutes</span>
              <input required type="number" min={15} max={480} step={15} value={f.minutes}
                onChange={(e) => setF({ ...f, minutes: e.target.value })} />
            </label>
            <label className="field">
              <span>Kind</span>
              <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}>
                {LESSON_KINDS.map((k) => (
                  <option key={k} value={k}>{titleCase(k)}</option>
                ))}
              </select>
            </label>
          </div>
          <div className="actions">
            <button className="gold" disabled={book.busy}>
              {book.busy ? 'Booking…' : `Book ${booking.from}`}
            </button>
            <button type="button" className="secondary" onClick={() => setBooking(null)}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </Card>
  );
}


/* ------------------------------------------------------------------ */
/* Keys for things that are not people                                 */
/* ------------------------------------------------------------------ */

interface DeviceRow {
  id: string;
  code: string;
  name: string;
  kind: string;
  scopes: string[];
  status: string;
  secret_last4: string;
  expires_on: string | null;
  last_used_at: string | null;
  last_used_ip: string | null;
  revoked_at: string | null;
  revoked_reason: string | null;
  is_live: boolean;
  days_quiet: number | null;
}

const DEVICE_KINDS: { id: string; label: string }[] = [
  { id: 'lightning_detector', label: 'Lightning detector' },
  { id: 'weather_station', label: 'Weather station' },
  { id: 'door_controller', label: 'Door controller' },
  { id: 'payment_gateway', label: 'Payment gateway' },
  { id: 'point_of_sale', label: 'Point of sale' },
  { id: 'other', label: 'Something else' },
];

/**
 * The one screen in this product that shows a secret, and it shows each
 * one exactly once.
 *
 * That is not a limitation to apologise for in a tooltip; it is the
 * reason the credential is safe to have at all, and the screen says so
 * in the place somebody is looking rather than in a help page. The key
 * sits in a mono block with `user-select: all` — the same treatment the
 * product already gives a value somebody has to copy — and it goes away
 * when they dismiss it.
 *
 * `last_used_at` is the column that earns the list. A detector that has
 * gone quiet looks exactly like a quiet week of weather until somebody
 * can see it has not spoken since April.
 */
function Devices() {
  const list = useApi<{ devices: DeviceRow[]; scopes: string[] }>(
    '/v1/devices?include_revoked=true');
  const [issued, setIssued] = useState<{ key: string; name: string } | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ code: '', name: '', kind: 'lightning_detector' });

  const issue = useMutation(async () => {
    const r = await api<{ key: string; device: DeviceRow }>('/v1/devices', {
      method: 'POST',
      body: {
        code: form.code.trim(),
        name: form.name.trim(),
        kind: form.kind,
        scopes: ['course.alert'],
      },
    });
    setIssued({ key: r.key, name: r.device.name });
    setAdding(false);
    setForm({ code: '', name: '', kind: 'lightning_detector' });
    list.reload();
  });

  const rotate = useMutation(async (d: DeviceRow) => {
    const r = await api<{ key: string }>(`/v1/devices/${d.id}/rotate`, { method: 'POST' });
    setIssued({ key: r.key, name: d.name });
    list.reload();
  });

  const revoke = useMutation(async (d: DeviceRow) => {
    await api(`/v1/devices/${d.id}`, {
      method: 'DELETE', body: { reason: 'Revoked from the devices screen.' },
    });
    setNote(`${d.name} is revoked. Its record stays.`);
    list.reload();
  });

  const suspend = useMutation(async (v: { d: DeviceRow; status: string }) => {
    await api(`/v1/devices/${v.d.id}`, { method: 'PATCH', body: { status: v.status } });
    setNote(v.status === 'suspended'
      ? `${v.d.name} is suspended. Its key stops working now and starts again when you `
        + 'switch it back on.'
      : `${v.d.name} is back on.`);
    list.reload();
  });

  const rows = list.data?.devices ?? [];
  const working = rows.filter((d) => d.status !== 'revoked');
  const revoked = rows.filter((d) => d.status === 'revoked');

  return (
    <>
      {list.loading && <Loading />}
      <ErrorBanner error={list.error ?? issue.error ?? rotate.error
        ?? revoke.error ?? suspend.error} />
      {note && <div className="notice">{note}</div>}

      {/* The one moment the secret exists in readable form. */}
      {issued && (
        <Card title={`The key for ${issued.name}`}>
          <p>
            Copy this into the device now. It is stored as a hash and <strong>cannot be
            shown again</strong> — if it is lost, rotate the device and set the new one.
          </p>
          <div className="secret">{issued.key}</div>
          <div className="actions" style={{ marginTop: 12 }}>
            <button className="secondary" onClick={() => setIssued(null)}>
              I have copied it
            </button>
          </div>
        </Card>
      )}

      <Card
        title="Devices"
        actions={!adding
          ? <button className="gold small" onClick={() => setAdding(true)}>Issue a key</button>
          : undefined}
      >
        <p className="muted small">
          A lightning detector has no login and never will. This is what it presents
          instead: an id that travels in the clear and a secret that is hashed the same
          way a door code is.
        </p>

        {adding && (
          <form className="form-rows" onSubmit={(e) => { e.preventDefault(); issue.run(); }}>
            <label className="field">
              <span>Handle</span>
              <input value={form.code} required
                placeholder="lightning-1"
                onChange={(e) => setForm({ ...form, code: e.target.value })} />
              <span className="hint">
                Lower case, no spaces. This is how the key is recognized in a log.
              </span>
            </label>
            <label className="field">
              <span>Name</span>
              <input value={form.name} required
                placeholder="Thor Guard, sixteenth tee"
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <span className="hint">
                What a member sees on the banner when this thing reports a strike.
              </span>
            </label>
            <label className="field">
              <span>Kind</span>
              <select value={form.kind}
                onChange={(e) => setForm({ ...form, kind: e.target.value })}>
                {DEVICE_KINDS.map((k) => (
                  <option key={k.id} value={k.id}>{k.label}</option>
                ))}
              </select>
            </label>
            <div className="actions">
              <button className="gold" type="submit" disabled={issue.busy}>Issue it</button>
              <button className="secondary" type="button" onClick={() => setAdding(false)}>
                Cancel
              </button>
            </div>
          </form>
        )}

        {working.length === 0 && !adding ? (
          <Empty>
            No devices. Nothing is calling this club without a person behind it, which
            is the right number until the shop installs something.
          </Empty>
        ) : working.length > 0 ? (
          <table className="data">
            <thead>
              <tr>
                <th>Device</th><th>Kind</th><th>Key</th>
                <th>Last heard from</th><th>State</th><th />
              </tr>
            </thead>
            <tbody>
              {working.map((d) => (
                <tr key={d.id}>
                  <td>
                    {d.name}
                    <div className="hint mono">{d.code}</div>
                  </td>
                  <td className="small">
                    {DEVICE_KINDS.find((k) => k.id === d.kind)?.label ?? d.kind}
                  </td>
                  <td className="small mono">…{d.secret_last4}</td>
                  <td className="small">
                    {d.last_used_at ? dateTime(d.last_used_at)
                      : <span className="muted">never</span>}
                    {/* A week of silence from a lightning detector is either
                        a quiet week or a dead box, and only one of those is
                        fine. */}
                    {d.days_quiet !== null && d.days_quiet >= 7 && (
                      <div className="hint">
                        nothing for {d.days_quiet} days — worth checking
                      </div>
                    )}
                  </td>
                  <td>
                    <Status tone={d.is_live ? 'good' : 'warning'}>
                      {d.is_live ? 'Live' : titleCase(d.status)}
                    </Status>
                    {d.expires_on && (
                      <div className="hint">expires {fmtDate(d.expires_on)}</div>
                    )}
                  </td>
                  <td className="actions">
                    <button className="secondary small" disabled={rotate.busy}
                      onClick={() => rotate.run(d)}>
                      New key
                    </button>
                    <button className="secondary small" disabled={suspend.busy}
                      onClick={() => suspend.run({
                        d, status: d.status === 'suspended' ? 'active' : 'suspended',
                      })}>
                      {d.status === 'suspended' ? 'Switch on' : 'Suspend'}
                    </button>
                    <DeleteButton
                      label="Revoke"
                      confirmLabel="Revoke for good"
                      busy={revoke.busy}
                      onDelete={() => revoke.run(d)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </Card>

      {revoked.length > 0 && (
        <Card title="Revoked">
          <p className="muted small">
            Kept rather than deleted. &ldquo;What was that thing that was calling us in
            March&rdquo; is a question a deleted row cannot answer.
          </p>
          <table className="data">
            <thead>
              <tr><th>Device</th><th>Revoked</th><th>Why</th><th>Last heard from</th></tr>
            </thead>
            <tbody>
              {revoked.map((d) => (
                <tr key={d.id}>
                  <td>{d.name}<div className="hint mono">{d.code}</div></td>
                  <td className="small">{d.revoked_at ? dateTime(d.revoked_at) : '—'}</td>
                  <td className="small">{d.revoked_reason ?? '—'}</td>
                  <td className="small">
                    {d.last_used_at ? dateTime(d.last_used_at) : 'never'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </>
  );
}


/* ------------------------------------------------------------------ */
/* Cards, and the queue to the association                             */
/* ------------------------------------------------------------------ */

interface ScoreRow {
  score_id: string;
  person_id: string;
  player_name: string;
  ghin_number: string | null;
  played_on: string;
  course_name: string;
  tee_name: string;
  holes: number;
  status: string;
  gross: number | null;
  adjusted_gross: number | null;
  net: number | null;
  holes_played: number;
  index_used: string | null;
  course_handicap: number | null;
  differential: string | null;
  posting_status: string;
  is_acceptable: boolean;
  unacceptable_reason: string | null;
  attested_by_name: string | null;
  may_post: boolean;
  why_not: string | null;
  post_gross: number | null;
  post_differential: string | null;
}

interface HoleRow {
  playing_number: number;
  par: number | null;
  stroke_index: number | null;
  strokes_given: number | null;
  strokes: number | null;
  putts: number | null;
  adjusted: number | null;
}

interface CardPayload {
  score: ScoreRow;
  holes: HoleRow[];
  posting: {
    may_post: boolean; why_not: string | null;
    holes_required: number; holes_played: number;
    post_gross: number | null; post_differential: string | null;
  } | null;
}

/**
 * The shop's side of a handicap.
 *
 * What this screen is NOT is the point of it. It does not compute an
 * index and there is nowhere on it to type one — that comes from the
 * association, and migration 0063 spends a page explaining why a club
 * that shows a member its own arithmetic has built an argument rather
 * than a feature.
 *
 * What it does is get the card in accurately and show what will actually
 * be sent, which is frequently not what was shot: net double bogey cuts
 * the blow-up hole down, and a hole nobody finished posts as net par.
 * Both are visible here rather than being discovered by a member who
 * counted their own card and got a different number.
 */
function Scores({ canPost, canJudge }: { canPost: boolean; canJudge: boolean }) {
  const [reload, setReload] = useState(0);
  const queue = useApi<{ scores: ScoreRow[]; ready: number; stuck: number }>(
    '/v1/golf/scores/queue', [reload]);
  const [open, setOpen] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const bump = () => setReload((n) => n + 1);

  const send = useMutation(async (v: { id: string; state: string; reference?: string }) => {
    const r = await api<{ posted_gross: number; posted_differential: string }>(
      `/v1/golf/scores/${v.id}/post`,
      { method: 'POST', body: { state: v.state, reference: v.reference ?? null } },
    );
    setNote(
      v.state === 'sent'
        ? `Recorded as sent — ${r.posted_gross} played to a differential of `
          + `${r.posted_differential}.`
        : v.state === 'not_posted'
          ? 'Marked as one that will not be posted.'
          : 'Marked ready to send.',
    );
    bump();
  });

  const rows = queue.data?.scores ?? [];

  return (
    <>
      {queue.loading && <Loading />}
      <ErrorBanner error={queue.error ?? send.error} />
      {note && <div className="notice">{note}</div>}

      <Card
        title="Waiting to Go"
        actions={queue.data
          ? <span className="muted small">
              {queue.data.ready} ready · {queue.data.stuck} cannot go
            </span>
          : undefined}
      >
        <p className="muted small">
          The club records the round; the association revises the index. Nothing on
          this screen changes anybody&apos;s handicap — it gets the card right and
          shows what will be sent.
        </p>

        {rows.length === 0 ? (
          <Empty>
            No cards waiting. They arrive here as the shop enters them after a round.
          </Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Player</th><th>Day</th><th>Tee</th>
                <th className="num">Shot</th><th className="num">Posts as</th>
                <th className="num">Diff.</th><th>State</th><th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.score_id}>
                  <td>
                    {r.player_name}
                    {r.ghin_number && <div className="hint mono">{r.ghin_number}</div>}
                  </td>
                  <td className="small">
                    {fmtDate(r.played_on)}
                    {r.holes === 9 && <div className="hint">nine holes</div>}
                  </td>
                  <td className="small">{r.tee_name}</td>
                  <td className="num">
                    {r.gross ?? '—'}
                    <div className="hint">{r.holes_played} of {r.holes}</div>
                  </td>
                  {/* What actually goes. Different from the gross on most
                      cards, and the difference is the whole reason a club
                      cannot just send the number off the paper. */}
                  <td className="num">{r.post_gross ?? '—'}</td>
                  <td className="num mono">{r.post_differential ?? '—'}</td>
                  <td className="small">
                    {r.may_post
                      ? <Status tone="good">Ready</Status>
                      : <>
                          <Status tone="warning">Held</Status>
                          <div className="hint">{r.why_not}</div>
                        </>}
                    {!r.is_acceptable && (
                      <div className="hint">{r.unacceptable_reason}</div>
                    )}
                  </td>
                  <td className="actions">
                    <button className="secondary small"
                      onClick={() => setOpen(open === r.score_id ? null : r.score_id)}>
                      {open === r.score_id ? 'Close' : 'The card'}
                    </button>
                    {canPost && r.may_post && (
                      <button className="gold small" disabled={send.busy}
                        onClick={() => send.run({ id: r.score_id, state: 'sent' })}>
                        Sent to GHIN
                      </button>
                    )}
                    {canJudge && (
                      <button className="secondary small" disabled={send.busy}
                        onClick={() => send.run({ id: r.score_id, state: 'not_posted' })}>
                        Will not post
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {open && <ScoreCard id={open} canEdit={canPost} onChange={bump} />}
    </>
  );
}

/**
 * One card, hole by hole.
 *
 * A row of eighteen boxes, because that is the shape of the thing being
 * copied and any other layout makes somebody count along with a finger.
 * Blank means the hole was not played — not zero, which sums into a round
 * of somebody's life (GOLF-16), and the input refuses to become one.
 */
function ScoreCard({
  id, canEdit, onChange,
}: { id: string; canEdit: boolean; onChange: () => void }) {
  const [reload, setReload] = useState(0);
  const card = useApi<CardPayload>(`/v1/golf/scores/${id}`, [id, reload]);
  const [draft, setDraft] = useState<Record<number, string>>({});
  const [dirty, setDirty] = useState(false);

  const save = useMutation(async () => {
    const holes = card.data?.holes ?? [];
    const strokes = holes.map((h) => {
      const raw = draft[h.playing_number];
      const v = raw === undefined ? h.strokes : (raw.trim() === '' ? null : Number(raw));
      return v === null || Number.isNaN(v) ? null : v;
    });
    await api(`/v1/golf/scores/${id}/holes`, { method: 'PUT', body: { strokes } });
    setDraft({});
    setDirty(false);
    setReload((n) => n + 1);
    onChange();
  });

  const c = card.data;
  if (card.loading || !c) return <Card><Loading /></Card>;

  const valueFor = (h: HoleRow) =>
    draft[h.playing_number] ?? (h.strokes === null ? '' : String(h.strokes));

  const front = c.holes.filter((h) => h.playing_number <= 9);
  const back = c.holes.filter((h) => h.playing_number > 9);

  // "Out" and "In", not "Out" twice. A scorecard has said this for a
  // century and a golfer reads the word before the number.
  const nine = (holes: HoleRow[], label: string, total: string) => (
    <div style={{ overflowX: 'auto', marginBottom: 12 }}>
      <table className="data">
        <thead>
          <tr>
            <th>{label}</th>
            {holes.map((h) => <th key={h.playing_number} className="num">
              {h.playing_number}
            </th>)}
            <th className="num">{total}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="small muted">Par</td>
            {holes.map((h) => <td key={h.playing_number} className="num small">{h.par}</td>)}
            <td className="num small">
              {holes.reduce((t, h) => t + (h.par ?? 0), 0)}
            </td>
          </tr>
          <tr>
            <td className="small muted">Index</td>
            {holes.map((h) => (
              <td key={h.playing_number} className="num small muted">{h.stroke_index}</td>
            ))}
            <td />
          </tr>
          <tr>
            <td className="small muted">Strokes</td>
            {holes.map((h) => (
              <td key={h.playing_number} className="num small muted">
                {h.strokes_given ? h.strokes_given : '—'}
              </td>
            ))}
            <td />
          </tr>
          <tr>
            <td className="small">Shot</td>
            {holes.map((h) => (
              <td key={h.playing_number} className="num">
                {canEdit ? (
                  <input
                    style={{ width: 44, textAlign: 'right' }}
                    inputMode="numeric"
                    value={valueFor(h)}
                    onChange={(e) => {
                      setDraft({ ...draft, [h.playing_number]: e.target.value });
                      setDirty(true);
                    }}
                  />
                ) : (h.strokes ?? '—')}
              </td>
            ))}
            <td className="num">
              {holes.reduce((t, h) => t + (h.strokes ?? 0), 0) || '—'}
            </td>
          </tr>
          {/* Only where it differs. A row of identical numbers under the
              scores is noise; a number that has been cut down is the
              thing somebody will ask about. */}
          {holes.some((h) => h.adjusted !== null && h.strokes !== null
            && h.adjusted < h.strokes) && (
            <tr>
              <td className="small muted">Posts as</td>
              {holes.map((h) => (
                <td key={h.playing_number} className="num small">
                  {h.adjusted !== null && h.strokes !== null && h.adjusted < h.strokes
                    ? <strong>{h.adjusted}</strong>
                    : <span className="muted">·</span>}
                </td>
              ))}
              <td />
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <Card
      title={`${c.score.player_name} — ${fmtDate(c.score.played_on)}`}
      actions={canEdit && dirty
        ? <button className="gold small" disabled={save.busy} onClick={() => save.run()}>
            Save the card
          </button>
        : undefined}
    >
      <ErrorBanner error={card.error ?? save.error} />

      <div className="tiles">
        <Tile label="Index used" value={c.score.index_used ?? '—'}
          note="on the day of play" />
        <Tile label="Course handicap" value={c.score.course_handicap ?? '—'}
          note={c.score.tee_name} />
        <Tile label="Shot" value={c.score.gross ?? '—'}
          note={`${c.score.holes_played} of ${c.score.holes} holes`} />
        <Tile label="Posts as" value={c.posting?.post_gross ?? '—'}
          note="after adjustment" />
        <Tile label="Differential" value={c.posting?.post_differential ?? '—'}
          note="before the association's PCC" />
      </div>

      {c.posting && !c.posting.may_post && (
        <Status tone="warning">{c.posting.why_not}</Status>
      )}

      {front.length > 0 && nine(front, 'Front', 'Out')}
      {back.length > 0 && nine(back, 'Back', 'In')}

      <p className="hint">
        A blank is a hole that was not played, which is not a zero. Once at least
        {' '}{c.posting?.holes_required ?? 14} holes are in, the blanks post as net par —
        which is why &ldquo;posts as&rdquo; can be higher than what was shot.
      </p>

      {c.score.attested_by_name && (
        <p className="hint">Attested by {c.score.attested_by_name}.</p>
      )}
    </Card>
  );
}


/* ------------------------------------------------------------------ */
/* The course, and the people who look after it                        */
/* ------------------------------------------------------------------ */

interface TaskRow {
  task_id: string;
  course_id: string | null;
  course_name: string | null;
  playing_number: number | null;
  element: string;
  title: string;
  detail: string | null;
  status: string;
  priority: string;
  scheduled_for: string | null;
  started_at: string | null;
  completed_at: string | null;
  outcome: string | null;
  assigned_to: string | null;
  assigned_to_name: string | null;
  requested_by_name: string | null;
  condition_id: string | null;
  condition_note: string | null;
  condition_kind: string | null;
  condition_still_open: boolean | null;
  days_old: number;
}

interface BoardPayload {
  tasks: TaskRow[];
  urgent: number;
  today: number;
  holding_a_notice: number;
}

const ELEMENTS = [
  'general', 'green', 'tee', 'fairway', 'bunker', 'rough',
  'cart_path', 'practice', 'irrigation', 'equipment', 'clubhouse',
];

const NOTICE_KINDS = [
  'temporary_green', 'temporary_tee', 'ground_under_repair', 'out_of_play',
  'cart_path_only', 'preferred_lies', 'closed', 'other',
];

/**
 * The shed's board, and the notices it put up.
 *
 * Two panels because they are two different things (0115) and the
 * separation is the point: the conditions panel is what every member can
 * read, the board below it is the club's own work and carries names,
 * judgements and money. They are shown together here because one person
 * does both jobs, and kept apart everywhere else because two audiences
 * read them.
 */
function Crew({ canEdit }: { canEdit: boolean }) {
  const [reload, setReload] = useState(0);
  const board = useApi<BoardPayload>(
    `/v1/golf/maintenance?include_done=false&on=${today()}`, [reload]);
  const courses = useApi<{ courses: { id: string; name: string }[] }>('/v1/golf/courses');
  const [note, setNote] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [finishing, setFinishing] = useState<TaskRow | null>(null);

  const bump = () => setReload((n) => n + 1);

  const raise = useMutation(async (v: Record<string, unknown>) => {
    await api('/v1/golf/maintenance', { method: 'POST', body: v });
    setAdding(false);
    setNote(v.condition ? 'Job raised, and the notice is up.' : 'Job raised.');
    bump();
  });

  const move = useMutation(async (v: { id: string; status: string }) => {
    await api(`/v1/golf/maintenance/${v.id}`, {
      method: 'PATCH', body: { status: v.status },
    });
    bump();
  });

  const finish = useMutation(async (v: { id: string; outcome: string; reopen: boolean }) => {
    await api(`/v1/golf/maintenance/${v.id}/done`, {
      method: 'POST', body: { outcome: v.outcome || null, reopen: v.reopen },
    });
    setFinishing(null);
    setNote(v.reopen
      ? 'Done, and the hole is back in play.'
      : 'Done. The notice is still up — take it down when the ground is ready.');
    bump();
  });

  const b = board.data;
  const courseList = courses.data?.courses ?? [];

  return (
    <>
      {board.loading && <Loading />}
      <ErrorBanner error={board.error ?? raise.error ?? move.error ?? finish.error} />
      {note && <div className="notice">{note}</div>}

      {/* What the members are being told. Shown to the shop unchanged,
          so that nobody has to guess what a notice looks like from the
          outside before they write one. */}
      <CourseConditions title="What the members are being told" reloadKey={reload} />

      <Card
        title="The Board"
        actions={canEdit && !adding
          ? <button className="gold small" onClick={() => setAdding(true)}>Raise a job</button>
          : undefined}
      >
        {b && (
          <div className="tiles">
            <Tile label="Urgent" value={b.urgent}
              note={b.urgent ? 'waiting' : 'nothing outstanding'} />
            <Tile label="On today" value={b.today} note="scheduled or under way" />
            {/* The link the whiteboard never had. */}
            <Tile label="Holding a notice" value={b.holding_a_notice}
              note="jobs keeping ground closed" />
          </div>
        )}

        {adding && (
          <CrewJobForm
            courses={courseList}
            busy={raise.busy}
            onCancel={() => setAdding(false)}
            onSubmit={(v) => raise.run(v)}
          />
        )}

        {!b || b.tasks.length === 0 ? (
          <Empty>
            Nothing outstanding. A job gets raised here rather than on the whiteboard so
            that the notice on the course and the work behind it stay attached.
          </Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Job</th><th>Where</th><th>Who</th>
                <th>When</th><th>Notice</th><th />
              </tr>
            </thead>
            <tbody>
              {b.tasks.map((t) => (
                <tr key={t.task_id} className={`crew__row--${t.priority}`}>
                  <td>
                    {t.title}
                    {t.detail && <div className="hint">{t.detail}</div>}
                    {t.priority !== 'routine' && (
                      <div className="hint">
                        <Status tone={t.priority === 'urgent' ? 'critical' : 'warning'}>
                          {titleCase(t.priority)}
                        </Status>
                      </div>
                    )}
                  </td>
                  <td className="small">
                    {t.playing_number !== null
                      ? `Hole ${t.playing_number}`
                      : t.course_name ?? '—'}
                    {t.element !== 'general' && (
                      <div className="hint">{t.element.replace(/_/g, ' ')}</div>
                    )}
                  </td>
                  <td className="small">
                    {t.assigned_to_name ?? <span className="muted">unassigned</span>}
                  </td>
                  <td className="small">
                    {t.status === 'in_progress'
                      ? <Status tone="good">Under way</Status>
                      : t.scheduled_for ? fmtDate(t.scheduled_for)
                        : <span className="muted">not scheduled</span>}
                    {/* The column a board is actually read for. */}
                    {t.days_old > 14 && (
                      <div className="hint">raised {t.days_old} days ago</div>
                    )}
                  </td>
                  <td className="small">
                    {t.condition_still_open
                      ? <>
                          <Status tone="warning">
                            {CONDITION_LABEL[t.condition_kind ?? ''] ?? 'Up'}
                          </Status>
                          <div className="hint">{t.condition_note}</div>
                        </>
                      : <span className="muted">—</span>}
                  </td>
                  <td className="actions">
                    {canEdit && t.status !== 'in_progress' && (
                      <button className="secondary small" disabled={move.busy}
                        onClick={() => move.run({ id: t.task_id, status: 'in_progress' })}>
                        Start
                      </button>
                    )}
                    {canEdit && (
                      <button className="gold small"
                        onClick={() => setFinishing(t)}>
                        Done
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {finishing && (
        <FinishJob
          task={finishing}
          busy={finish.busy}
          onCancel={() => setFinishing(null)}
          onSubmit={(v) => finish.run({ id: finishing.task_id, ...v })}
        />
      )}
    </>
  );
}

/** Raising a job, and putting the notice up in the same act. */
function CrewJobForm({
  courses, busy, onSubmit, onCancel,
}: {
  courses: { id: string; name: string }[];
  busy: boolean;
  onSubmit: (v: Record<string, unknown>) => void;
  onCancel: () => void;
}) {
  const [v, setV] = useState({
    title: '', detail: '', element: 'general', priority: 'routine',
    course_id: '', hole: '', scheduled_for: '',
  });
  const [notice, setNotice] = useState(false);
  const [n, setN] = useState({ kind: 'ground_under_repair', note: '' });

  const holesOn = useApi<{ holes: { playing_number: number; hole_id: string }[] }>(
    v.course_id ? `/v1/golf/courses/${v.course_id}/card` : null, [v.course_id]);

  const holeId = (holesOn.data?.holes ?? [])
    .find((h) => String(h.playing_number) === v.hole)?.hole_id;

  return (
    <form
      className="form-rows"
      style={{ maxWidth: 620, marginBottom: 14 }}
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({
          title: v.title.trim(),
          detail: v.detail.trim() || null,
          element: v.element,
          priority: v.priority,
          course_id: v.course_id || null,
          hole_id: holeId ?? null,
          scheduled_for: v.scheduled_for || null,
          status: v.scheduled_for ? 'scheduled' : 'requested',
          condition: notice && n.note.trim()
            ? { kind: n.kind, note: n.note.trim() }
            : null,
        });
      }}
    >
      <label className="field">
        <span>The job</span>
        <input required value={v.title} placeholder="Re-turf the fifth green"
          onChange={(e) => setV({ ...v, title: e.target.value })} />
      </label>

      <label className="field">
        <span>Detail</span>
        <input value={v.detail}
          placeholder="Anything the crew needs. Members never see this."
          onChange={(e) => setV({ ...v, detail: e.target.value })} />
        <span className="hint">
          Internal. What a member reads is the notice below, if you put one up.
        </span>
      </label>

      <div className="grid-2">
        <label className="field">
          <span>Course</span>
          <select value={v.course_id}
            onChange={(e) => setV({ ...v, course_id: e.target.value, hole: '' })}>
            <option value="">Not on a course</option>
            {courses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>

        <label className="field">
          <span>Hole</span>
          <select value={v.hole} disabled={!v.course_id}
            onChange={(e) => setV({ ...v, hole: e.target.value })}>
            <option value="">Whole course</option>
            {(holesOn.data?.holes ?? []).map((h) => (
              <option key={h.playing_number} value={String(h.playing_number)}>
                {h.playing_number}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span>Part</span>
          <select value={v.element}
            onChange={(e) => setV({ ...v, element: e.target.value })}>
            {ELEMENTS.map((el) => (
              <option key={el} value={el}>{el.replace(/_/g, ' ')}</option>
            ))}
          </select>
        </label>

        <label className="field">
          <span>Priority</span>
          <select value={v.priority}
            onChange={(e) => setV({ ...v, priority: e.target.value })}>
            <option value="routine">Routine</option>
            <option value="soon">Soon</option>
            <option value="urgent">Urgent</option>
          </select>
        </label>

        <label className="field">
          <span>Scheduled for</span>
          <input type="date" value={v.scheduled_for}
            onChange={(e) => setV({ ...v, scheduled_for: e.target.value })} />
        </label>
      </div>

      {/* The two acts in one form, because they are one decision. A shop
          that has to raise the job and then go and write the notice
          writes one of them. */}
      <label className="field">
        <span>
          <input type="checkbox" checked={notice}
            onChange={(e) => setNotice(e.target.checked)} />
          {' '}This closes or changes something members will meet
        </span>
      </label>

      {notice && (
        <div className="grid-2">
          <label className="field">
            <span>What to call it</span>
            <select value={n.kind} onChange={(e) => setN({ ...n, kind: e.target.value })}>
              {NOTICE_KINDS.map((k) => (
                <option key={k} value={k}>{CONDITION_LABEL[k] ?? k}</option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>What a member reads</span>
            <input value={n.note} required={notice}
              placeholder="Fifth closed while the green is re-turfed."
              onChange={(e) => setN({ ...n, note: e.target.value })} />
          </label>
        </div>
      )}

      <div className="actions">
        <button className="gold" type="submit" disabled={busy}>Raise it</button>
        <button className="secondary" type="button" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

/**
 * Finishing a job, and the tick that reopens the ground.
 *
 * Off by default and deliberately its own decision. Turf that has been
 * laid is not turf that has knitted; a green flagged clear too early is
 * a member walking onto it, and that is the only failure here with a
 * cost attached.
 */
function FinishJob({
  task, busy, onSubmit, onCancel,
}: {
  task: TaskRow;
  busy: boolean;
  onSubmit: (v: { outcome: string; reopen: boolean }) => void;
  onCancel: () => void;
}) {
  const [outcome, setOutcome] = useState('');
  const [reopen, setReopen] = useState(false);

  return (
    <Card title={`Finished — ${task.title}`}>
      <form
        className="form-rows"
        onSubmit={(e) => { e.preventDefault(); onSubmit({ outcome, reopen }); }}
      >
        <label className="field">
          <span>What was done</span>
          <input value={outcome} placeholder="Turf laid, roped off."
            onChange={(e) => setOutcome(e.target.value)} />
        </label>

        {task.condition_still_open && (
          <>
            <label className="field">
              <span>
                <input type="checkbox" checked={reopen}
                  onChange={(e) => setReopen(e.target.checked)} />
                {' '}Take the notice down as well
              </span>
            </label>
            <p className="hint" style={{ maxWidth: '58ch' }}>
              &ldquo;{task.condition_note}&rdquo; is still showing to members. Leave it up
              if the ground is not ready to walk on — a green flagged clear while the turf
              is still knitting is somebody walking onto it.
            </p>
          </>
        )}

        <div className="actions">
          <button className="gold" type="submit" disabled={busy}>Mark it done</button>
          <button className="secondary" type="button" onClick={onCancel}>Cancel</button>
        </div>
      </form>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* The page                                                            */
/* ------------------------------------------------------------------ */

export function GolfAdminPage() {
  const { atLeast } = useSession();
  const [tab, setTab] = useState<Tab>('today');

  // The API enforces all of these. These flags only decide whether to offer
  // an action the server would refuse.
  const isController = atLeast('controller');
  const isManager = atLeast('manager');
  const isStaff = atLeast('staff');
  const isAdmin = atLeast('club_admin');
  const tabs = isAdmin ? [...TABS, DEVICES_TAB] : TABS;

  return (
    <>
      <div className="row" style={{ marginBottom: 16 }}>
        <div>
          <h1>Golf Administration</h1>
          <div className="muted small">
            The settings the shop actually changes — what a round costs, what time the first group
            goes off, where the carts are — and what any of it earned.
          </div>
        </div>
      </div>

      <div className="row" style={{ marginBottom: 16 }}>
        {tabs.map((t) => (
          <button key={t.id}
            className={t.id === tab ? 'gold small' : 'secondary small'}
            onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'today' && (
        <>
          {/* Above the frost decision, because both answer "what is the
              course doing today" and only one of them has minutes in it. */}
          <CourseAlerts canAct={isStaff} />
          {/* Above the tee sheet, because "where is everybody" is asked
              before "who is booked" once the day has started. */}
          <OnTheCourse />
          <Today canDecide={isStaff} canDelete={isManager} />
          <ConditionRules canEdit={isManager} />
        </>
      )}
      {tab === 'devices' && <Devices />}
      {tab === 'scores' && <Scores canPost={isStaff} canJudge={isManager} />}
      {tab === 'crew' && <Crew canEdit={isStaff} />}
      {tab === 'events' && <Events canManage={isManager} canEnter={isStaff} />}
      {tab === 'series' && <Series canManage={isManager} />}
      {tab === 'stickers' && <Stickers canManage={isManager} />}
      {tab === 'seasons' && (
        <>
          <Seasons canEdit={isManager} />
          <Holidays canEdit={isManager} />
        </>
      )}
      {tab === 'rates' && <Rates canEdit={isController} />}
      {tab === 'fleet' && <Fleet canEdit={isStaff} canDelete={isManager} />}
      {tab === 'lessons' && (
        <>
          <Professionals canManage={isManager} canBook={isStaff} />
          <Lessons canEdit={isStaff} />
        </>
      )}
      {tab === 'practice' && <Practice canBook={isStaff} canManage={isStaff} />}
      {tab === 'numbers' && <Numbers />}
      {tab === 'revenue' && <Revenue />}
    </>
  );
}
